<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="website-leads-page">
    <section class="leads-hero">
      <div class="leads-hero__glow" aria-hidden="true"></div>
      <div class="leads-hero__content">
        <div class="leads-identity">
          <span class="leads-identity__icon">
            <v-icon size="25">mdi-message-text-fast-outline</v-icon>
          </span>
          <div>
            <span class="leads-eyebrow">{{ $t('websiteLeads.eyebrow') }}</span>
            <h1>{{ $t('websiteLeads.title') }}</h1>
            <p>{{ $t('websiteLeads.subtitle') }}</p>
          </div>
        </div>

        <div class="leads-hero__actions">
          <div class="leads-inbox-state" :class="{ 'has-new': newCount > 0 }">
            <span class="leads-inbox-state__dot"></span>
            <span>
              <small>{{ $t('websiteLeads.inboxLabel') }}</small>
              <strong>{{ newCount }} {{ $t('websiteLeads.newShort') }}</strong>
            </span>
          </div>
          <button
            type="button"
            class="leads-refresh"
            :disabled="isRefreshing"
            @click="refreshAll"
          >
            <v-icon size="17" :class="{ 'is-spinning': isRefreshing }">mdi-refresh</v-icon>
            {{ $t('common.refresh') }}
          </button>
        </div>
      </div>
    </section>

    <section v-if="stats" class="leads-metrics" :aria-label="$t('websiteLeads.stats.sectionLabel')">
      <article
        v-for="metric in metricCards"
        :key="metric.key"
        class="leads-metric"
        :class="`leads-metric--${metric.tone}`"
      >
        <span class="leads-metric__icon"><v-icon size="19">{{ metric.icon }}</v-icon></span>
        <div class="leads-metric__copy">
          <small>{{ metric.label }}</small>
          <strong>{{ metric.value }}</strong>
          <span>{{ metric.hint }}</span>
        </div>
      </article>
    </section>
    <section v-else class="leads-metrics leads-metrics--loading" aria-live="polite">
      <div v-for="n in 4" :key="n" class="leads-metric-skeleton"></div>
    </section>

    <section v-if="stats" class="leads-insights">
      <article class="leads-insight-card leads-status-overview">
        <header class="leads-card-head">
          <span class="leads-card-head__icon"><v-icon size="18">mdi-chart-donut</v-icon></span>
          <div>
            <h2>{{ $t('websiteLeads.statusOverviewTitle') }}</h2>
            <p>{{ $t('websiteLeads.statusOverviewSubtitle') }}</p>
          </div>
        </header>

        <div class="leads-status-list">
          <button
            v-for="entry in statusSegments"
            :key="entry.value"
            type="button"
            class="leads-status-row"
            :class="[`leads-status-row--${entry.tone}`, { 'is-active': statusFilter === entry.value }]"
            @click="statusFilter = entry.value"
          >
            <span class="leads-status-row__dot"></span>
            <span class="leads-status-row__label">{{ entry.label }}</span>
            <span class="leads-status-row__track">
              <i :style="{ width: statusBarWidth(entry.count) }"></i>
            </span>
            <strong>{{ entry.count }}</strong>
          </button>
        </div>
      </article>

      <article class="leads-insight-card leads-chart">
        <header class="leads-card-head">
          <span class="leads-card-head__icon leads-card-head__icon--violet">
            <v-icon size="18">mdi-chart-timeline-variant-shimmer</v-icon>
          </span>
          <div>
            <h2>{{ $t('websiteLeads.stats.chartTitle') }}</h2>
            <p>{{ $t('websiteLeads.stats.chartSubtitle') }}</p>
          </div>
          <span class="leads-chart__summary">
            <small>{{ $t('websiteLeads.stats.last30Short') }}</small>
            <strong>{{ stats.last30 }}</strong>
          </span>
        </header>

        <template v-if="stats.daily && stats.daily.length">
          <div class="leads-chart-bars">
            <div
              v-for="d in stats.daily"
              :key="d.day"
              class="leads-bar-wrap"
              :title="formatDay(d.day) + ': ' + d.count"
            >
              <div
                class="leads-bar"
                :class="{ 'leads-bar--zero': d.count === 0 }"
                :style="{ height: barHeight(d.count) }"
              ></div>
            </div>
          </div>
          <div class="leads-chart-axis">
            <span>{{ formatDay(stats.daily[0].day) }}</span>
            <span>{{ $t('websiteLeads.stats.todayShort') }}</span>
          </div>
        </template>
        <div v-else class="leads-chart-empty">
          <v-icon size="24">mdi-chart-bar</v-icon>
          <span>{{ $t('websiteLeads.stats.noChartData') }}</span>
        </div>
      </article>
    </section>

    <section class="leads-workspace">
      <header class="leads-workspace__header">
        <div>
          <span class="leads-section-eyebrow">{{ $t('websiteLeads.listEyebrow') }}</span>
          <h2>{{ isContactsView ? $t('websiteLeads.contacts.listTitle') : $t('websiteLeads.listTitle') }}</h2>
          <p>{{ isContactsView ? $t('websiteLeads.contacts.listSubtitle') : $t('websiteLeads.listSubtitle') }}</p>
        </div>
        <v-text-field
          v-model="search"
          :placeholder="$t('websiteLeads.searchPlaceholder')"
          prepend-inner-icon="mdi-magnify"
          clearable
          outlined
          dense
          hide-details
          class="leads-search"
        />
      </header>

      <nav class="leads-tabs" :aria-label="$t('websiteLeads.filterLabel')">
        <button
          v-for="tab in statusTabs"
          :key="tab.value"
          type="button"
          class="leads-tab"
          :class="[`leads-tab--${tab.tone}`, { 'is-active': statusFilter === tab.value }]"
          @click="statusFilter = tab.value"
        >
          <v-icon size="16">{{ tab.icon }}</v-icon>
          <span>{{ tab.label }}</span>
          <small>{{ tab.count }}</small>
        </button>
        <button
          type="button"
          class="leads-tab leads-tab--contacts"
          :class="{ 'is-active': isContactsView }"
          @click="statusFilter = 'CONTACTS'"
        >
          <v-icon size="16">mdi-card-account-phone-outline</v-icon>
          <span>{{ $t('websiteLeads.contacts.tab') }}</span>
          <small>{{ contactsCount }}</small>
        </button>
      </nav>

      <div class="leads-results-bar">
        <span>
          <v-icon size="16">{{ isContactsView ? 'mdi-card-account-phone-outline' : 'mdi-inbox-arrow-down-outline' }}</v-icon>
          {{ isContactsView ? $t('websiteLeads.contacts.resultsPrefix') : $t('websiteLeads.resultsPrefix') }}
          <strong>{{ isContactsView ? contacts.length : leads.length }}</strong>
        </span>
        <span v-if="search" class="leads-results-bar__search">
          {{ $t('websiteLeads.searchingFor') }} „{{ search }}”
        </span>
        <button
          v-if="isContactsView"
          type="button"
          class="leads-refresh leads-export"
          :disabled="!contacts.length"
          @click="exportContactsCsv"
        >
          <v-icon size="16">mdi-download-outline</v-icon>
          {{ $t('websiteLeads.contacts.exportCsv') }}
        </button>
      </div>

      <div v-if="isContactsView && selectedContacts.length" class="contacts-bulk-bar">
        <span class="contacts-bulk-bar__info">
          <v-icon size="16">mdi-checkbox-marked-outline</v-icon>
          {{ $t('websiteLeads.contacts.bulk.selected') }}
          <strong>{{ selectedContacts.length }}</strong>
        </span>
        <button
          type="button"
          class="lead-primary-action lead-primary-action--progress"
          :disabled="!bulkSmsRecipients.length"
          @click="openBulkReply('SMS')"
        >
          <v-icon size="15">mdi-message-text-outline</v-icon>
          {{ $t('websiteLeads.contacts.bulk.smsBtn') }}
        </button>
        <button
          type="button"
          class="lead-primary-action lead-primary-action--neutral"
          :disabled="!bulkEmailRecipients.length"
          @click="openBulkReply('EMAIL')"
        >
          <v-icon size="15">mdi-email-arrow-right-outline</v-icon>
          {{ $t('websiteLeads.contacts.bulk.emailBtn') }}
        </button>
        <button type="button" class="contacts-bulk-clear" @click="selectedContacts = []">
          <v-icon size="15">mdi-close</v-icon>
          {{ $t('websiteLeads.contacts.bulk.clear') }}
        </button>
      </div>

      <div class="leads-table-shell">
        <v-data-table
          v-if="isContactsView"
          v-model="selectedContacts"
          :headers="contactsHeaders"
          :items="contacts"
          :loading="contactsLoading"
          :search="search"
          :items-per-page="25"
          :mobile-breakpoint="700"
          :footer-props="{ 'items-per-page-options': [10, 25, 50], 'items-per-page-text': $t('common.perPage') }"
          class="leads-table"
          :loading-text="$t('common.loading')"
          :no-data-text="$t('common.noData')"
          item-key="key"
          show-select
          sort-by="lastAt"
          sort-desc
        >
          <template v-slot:item.name="{ item }">
            <button type="button" class="lead-person" @click="openLead(item.lead)">
              <span class="lead-avatar">{{ leadInitials(item.name) }}</span>
              <span class="lead-person__copy">
                <strong>{{ item.name }}</strong>
                <small>
                  <v-icon size="12">mdi-map-marker-outline</v-icon>
                  {{ item.city || $t('websiteLeads.noCity') }}
                </small>
              </span>
            </button>
          </template>

          <template v-slot:item.phone="{ item }">
            <div class="lead-contact">
              <a :href="'tel:' + item.phone">
                <span><v-icon size="14">mdi-phone-outline</v-icon></span>
                {{ item.phone }}
              </a>
            </div>
          </template>

          <template v-slot:item.email="{ item }">
            <div class="lead-contact">
              <a v-if="item.email" :href="'mailto:' + item.email">
                <span><v-icon size="14">mdi-email-outline</v-icon></span>
                {{ item.email }}
              </a>
              <span v-else class="lead-contact__empty">
                <v-icon size="14">mdi-email-off-outline</v-icon>{{ $t('websiteLeads.noEmail') }}
              </span>
            </div>
          </template>

          <template v-slot:item.leadsCount="{ item }">
            <div class="contact-meta">
              <span class="contact-count">{{ item.leadsCount }}</span>
              <time>{{ formatDateTime(item.lastAt) }}</time>
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="lead-actions contact-actions">
              <button
                type="button"
                class="lead-primary-action lead-primary-action--progress"
                @click="openReply(item.lead, 'SMS')"
              >
                <v-icon size="15">mdi-message-text-outline</v-icon>
                {{ $t('websiteLeads.contacts.smsAction') }}
              </button>
              <button
                type="button"
                class="lead-primary-action lead-primary-action--neutral"
                :disabled="!item.email"
                @click="openReply(item.lead, 'EMAIL')"
              >
                <v-icon size="15">mdi-email-arrow-right-outline</v-icon>
                {{ $t('websiteLeads.contacts.emailAction') }}
              </button>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="leads-empty">
              <span><v-icon size="30">mdi-card-account-phone-outline</v-icon></span>
              <strong>{{ $t('websiteLeads.contacts.emptyTitle') }}</strong>
              <p>{{ $t('websiteLeads.contacts.emptySubtitle') }}</p>
            </div>
          </template>
        </v-data-table>

        <v-data-table
          v-else
          :headers="computedHeaders"
          :items="leads"
          :loading="loading"
          :search="search"
          :items-per-page="25"
          :mobile-breakpoint="700"
          :footer-props="{ 'items-per-page-options': [10, 25, 50], 'items-per-page-text': $t('common.perPage') }"
          class="leads-table"
          :loading-text="$t('common.loading')"
          :no-data-text="$t('common.noData')"
          @click:row="openLead"
        >
          <template v-slot:item.createdAt="{ item }">
            <button type="button" class="lead-person" @click.stop="openLead(item)">
              <span class="lead-avatar">{{ leadInitials(item.name) }}</span>
              <span class="lead-person__copy">
                <strong>{{ item.name }}</strong>
                <small>
                  <v-icon size="12">mdi-map-marker-outline</v-icon>
                  {{ item.city || $t('websiteLeads.noCity') }}
                </small>
                <time>{{ formatDateTime(item.createdAt) }}</time>
              </span>
            </button>
          </template>

          <template v-slot:item.phone="{ item }">
            <div class="lead-contact">
              <a :href="'tel:' + item.phone" @click.stop>
                <span><v-icon size="14">mdi-phone-outline</v-icon></span>
                {{ item.phone }}
              </a>
              <a v-if="item.email" :href="'mailto:' + item.email" @click.stop>
                <span><v-icon size="14">mdi-email-outline</v-icon></span>
                {{ item.email }}
              </a>
              <span v-else class="lead-contact__empty">
                <v-icon size="14">mdi-email-off-outline</v-icon>{{ $t('websiteLeads.noEmail') }}
              </span>
            </div>
          </template>

          <template v-slot:item.message="{ item }">
            <button
              type="button"
              class="lead-preview"
              :class="{ 'is-empty': !item.message }"
              :title="$t('websiteLeads.showFull')"
              @click.stop="openLead(item)"
            >
              <v-icon size="15">mdi-message-text-outline</v-icon>
              <span>{{ item.message || $t('websiteLeads.noMessage') }}</span>
              <small>
                <template v-if="replyCount(item)">↩ {{ replyCount(item) }} · </template>{{ $t('websiteLeads.openDetails') }}
              </small>
            </button>
          </template>

          <template v-slot:item.note="{ item }">
            <button
              type="button"
              class="lead-note"
              :class="{ 'is-empty': !item.note }"
              :title="item.note ? $t('websiteLeads.noteEdit') : $t('websiteLeads.noteAdd')"
              @click.stop="editNote(item)"
            >
              <v-icon size="15">{{ item.note ? 'mdi-note-text-outline' : 'mdi-note-plus-outline' }}</v-icon>
              <span>
                <strong>{{ item.note || $t('websiteLeads.noteAdd') }}</strong>
                <small v-if="item.note">{{ noteMeta(item) }}</small>
              </span>
            </button>
          </template>

          <template v-slot:item.status="{ item }">
            <div class="lead-status-cell">
              <span class="lead-status" :class="`lead-status--${statusTone(item.status)}`">
                <i></i>{{ statusLabel(item.status) }}
              </span>
              <small v-if="item.status !== 'NEW' && item.handledBy">
                <v-icon size="12">mdi-account-check-outline</v-icon>{{ item.handledBy }}
              </small>
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="lead-actions" @click.stop>
              <button
                type="button"
                class="lead-primary-action"
                :class="`lead-primary-action--${primaryActionTone(item)}`"
                :disabled="updatingId === item.id"
                @click="setStatus(item, nextStatus(item))"
              >
                <v-progress-circular v-if="updatingId === item.id" indeterminate size="14" width="2" />
                <v-icon v-else size="15">{{ primaryActionIcon(item) }}</v-icon>
                {{ primaryActionLabel(item) }}
              </button>

              <v-menu offset-y left content-class="website-leads-menu">
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="lead-more"
                    :aria-label="$t('websiteLeads.moreActions')"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="19">mdi-dots-horizontal</v-icon>
                  </button>
                </template>
                <v-list dense>
                  <v-list-item @click="openLead(item)">
                    <v-list-item-icon><v-icon small>mdi-eye-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ $t('websiteLeads.openDetails') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="editNote(item)">
                    <v-list-item-icon><v-icon small>mdi-note-edit-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ item.note ? $t('websiteLeads.noteEdit') : $t('websiteLeads.noteAdd') }}</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                  <v-list-item @click="openReply(item, 'SMS')">
                    <v-list-item-icon><v-icon small color="#8EA7FF">mdi-message-reply-text-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ $t('websiteLeads.reply.smsAction') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item :disabled="!item.email" @click="openReply(item, 'EMAIL')">
                    <v-list-item-icon><v-icon small color="#8EA7FF">mdi-email-arrow-right-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ $t('websiteLeads.reply.emailAction') }}</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                  <v-subheader>{{ $t('websiteLeads.changeStatus') }}</v-subheader>
                  <v-list-item
                    v-for="status in availableStatuses(item)"
                    :key="status.value"
                    @click="setStatus(item, status.value)"
                  >
                    <v-list-item-icon>
                      <v-icon small :color="status.color">{{ status.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ status.label }}</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                  <v-list-item class="website-leads-menu__danger" @click="deleteLead(item)">
                    <v-list-item-icon><v-icon small color="error">mdi-trash-can-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ $t('common.delete') }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="leads-empty">
              <span><v-icon size="30">mdi-inbox-check-outline</v-icon></span>
              <strong>{{ $t('websiteLeads.emptyTitle') }}</strong>
              <p>{{ $t('websiteLeads.emptySubtitle') }}</p>
            </div>
          </template>
        </v-data-table>
      </div>
    </section>

    <v-dialog
      v-model="detailsDialog"
      max-width="760"
      scrollable
      content-class="website-lead-dialog"
    >
      <v-card v-if="selectedLead" class="website-lead-dialog__card">
        <header class="lead-dialog-head">
          <div class="lead-dialog-head__identity">
            <span class="lead-dialog-avatar">{{ leadInitials(selectedLead.name) }}</span>
            <div>
              <span>{{ $t('websiteLeads.detailsEyebrow') }}</span>
              <h2>{{ selectedLead.name }}</h2>
              <p>{{ formatDateTime(selectedLead.createdAt) }} · {{ sourceLabel(selectedLead.source) }}</p>
            </div>
          </div>
          <div class="lead-dialog-head__right">
            <span class="lead-status" :class="`lead-status--${statusTone(selectedLead.status)}`">
              <i></i>{{ statusLabel(selectedLead.status) }}
            </span>
            <button type="button" class="lead-dialog-close" :aria-label="$t('common.close')" @click="detailsDialog = false">
              <v-icon size="20">mdi-close</v-icon>
            </button>
          </div>
        </header>

        <v-card-text class="lead-dialog-body">
          <section class="lead-dialog-contact-grid">
            <a :href="'tel:' + selectedLead.phone">
              <span><v-icon size="18">mdi-phone-outline</v-icon></span>
              <small>{{ $t('websiteLeads.headers.phone') }}</small>
              <strong>{{ selectedLead.phone }}</strong>
              <v-icon size="15">mdi-arrow-top-right</v-icon>
            </a>
            <a v-if="selectedLead.email" :href="'mailto:' + selectedLead.email">
              <span><v-icon size="18">mdi-email-outline</v-icon></span>
              <small>{{ $t('websiteLeads.headers.email') }}</small>
              <strong>{{ selectedLead.email }}</strong>
              <v-icon size="15">mdi-arrow-top-right</v-icon>
            </a>
            <div v-else class="is-empty">
              <span><v-icon size="18">mdi-email-off-outline</v-icon></span>
              <small>{{ $t('websiteLeads.headers.email') }}</small>
              <strong>{{ $t('websiteLeads.noEmail') }}</strong>
            </div>
            <div>
              <span><v-icon size="18">mdi-map-marker-outline</v-icon></span>
              <small>{{ $t('websiteLeads.headers.city') }}</small>
              <strong>{{ selectedLead.city || $t('websiteLeads.noCity') }}</strong>
            </div>
          </section>

          <section class="lead-dialog-section">
            <header>
              <span><v-icon size="17">mdi-message-text-outline</v-icon></span>
              <div>
                <h3>{{ $t('websiteLeads.messageTitle') }}</h3>
                <p>{{ $t('websiteLeads.messageSubtitle') }}</p>
              </div>
              <button
                v-if="selectedLead.message && !selectedLead.messagePl"
                type="button"
                class="lead-dialog-edit"
                :disabled="translatingMessage"
                @click="translateLeadMessage"
              >
                <v-progress-circular v-if="translatingMessage" indeterminate size="13" width="2" />
                <v-icon v-else size="15">mdi-translate</v-icon>
                {{ $t('websiteLeads.translateMessage') }}
              </button>
            </header>
            <div class="lead-dialog-message" :class="{ 'is-empty': !selectedLead.message }">
              {{ selectedLead.message || $t('websiteLeads.noMessage') }}
            </div>
            <div v-if="selectedLead.messagePl" class="lead-dialog-message lead-dialog-message--translated">
              <small>
                <v-icon size="13">mdi-translate</v-icon>
                {{ $t('websiteLeads.translationTitle') }}
                <template v-if="selectedLead.messageLang && selectedLead.messageLang !== 'pl'">
                  · {{ $t('websiteLeads.translationFrom') }}: {{ langName(selectedLead.messageLang) }}
                </template>
              </small>
              {{ selectedLead.messagePl }}
            </div>
          </section>

          <section class="lead-dialog-section">
            <header>
              <span class="is-blue"><v-icon size="17">mdi-reply-outline</v-icon></span>
              <div>
                <h3>{{ $t('websiteLeads.reply.historyTitle') }}</h3>
                <p>{{ $t('websiteLeads.reply.historySubtitle') }}</p>
              </div>
              <button type="button" class="lead-dialog-edit" @click="openReply(selectedLead, 'SMS')">
                <v-icon size="15">mdi-message-reply-text-outline</v-icon>SMS
              </button>
              <button
                v-if="selectedLead.email"
                type="button"
                class="lead-dialog-edit"
                @click="openReply(selectedLead, 'EMAIL')"
              >
                <v-icon size="15">mdi-email-arrow-right-outline</v-icon>E-mail
              </button>
            </header>
            <div v-if="!replies.length" class="lead-dialog-note is-empty">
              {{ $t('websiteLeads.reply.historyEmpty') }}
            </div>
            <div v-else class="lead-reply-list">
              <article v-for="r in replies" :key="r.id" class="lead-reply-item" :class="{ 'is-failed': !r.ok }">
                <header>
                  <v-icon size="14">{{ r.channel === 'SMS' ? 'mdi-message-text-outline' : 'mdi-email-outline' }}</v-icon>
                  <strong>{{ r.channel === 'SMS' ? 'SMS' : 'E-mail' }} → {{ r.sentTo }}</strong>
                  <span v-if="r.lang && r.lang !== 'pl'" class="lead-reply-lang">{{ langName(r.lang) }}</span>
                  <span v-if="!r.ok" class="lead-reply-failed">{{ $t('websiteLeads.reply.failed') }}</span>
                  <time>{{ formatDateTime(r.createdAt) }}</time>
                </header>
                <p>{{ r.sentText }}</p>
                <small v-if="r.originalText">{{ $t('websiteLeads.reply.original') }}: {{ r.originalText }}</small>
                <small v-if="r.sentBy" class="lead-reply-by">{{ r.sentBy }}</small>
              </article>
            </div>
          </section>

          <section class="lead-dialog-section">
            <header>
              <span class="is-amber"><v-icon size="17">mdi-note-text-outline</v-icon></span>
              <div>
                <h3>{{ $t('websiteLeads.noteTitle') }}</h3>
                <p>{{ selectedLead.note ? noteMeta(selectedLead) : $t('websiteLeads.noNoteSubtitle') }}</p>
              </div>
              <button type="button" class="lead-dialog-edit" @click="editNote(selectedLead)">
                <v-icon size="15">{{ selectedLead.note ? 'mdi-pencil-outline' : 'mdi-plus' }}</v-icon>
                {{ selectedLead.note ? $t('websiteLeads.noteEdit') : $t('websiteLeads.noteAdd') }}
              </button>
            </header>
            <div class="lead-dialog-note" :class="{ 'is-empty': !selectedLead.note }">
              {{ selectedLead.note || $t('websiteLeads.noNote') }}
            </div>
          </section>

          <div v-if="selectedLead.handledBy" class="lead-dialog-owner">
            <span><v-icon size="17">mdi-account-check-outline</v-icon></span>
            <div>
              <small>{{ $t('websiteLeads.lastHandledBy') }}</small>
              <strong>{{ selectedLead.handledBy }}</strong>
            </div>
            <time v-if="selectedLead.handledAt">{{ formatDateTime(selectedLead.handledAt) }}</time>
          </div>
        </v-card-text>

        <v-card-actions class="lead-dialog-actions">
          <button type="button" class="lead-dialog-delete" @click="deleteLead(selectedLead)">
            <v-icon size="16">mdi-trash-can-outline</v-icon>{{ $t('common.delete') }}
          </button>
          <span></span>
          <button
            v-for="status in availableStatuses(selectedLead)"
            :key="status.value"
            type="button"
            class="lead-dialog-status-action"
            :class="`is-${status.tone}`"
            :disabled="updatingId === selectedLead.id"
            @click="setStatus(selectedLead, status.value)"
          >
            <v-icon size="16">{{ status.icon }}</v-icon>{{ status.label }}
          </button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="replyDialog"
      max-width="640"
      scrollable
      content-class="website-lead-dialog"
    >
      <v-card v-if="replyLead || bulkMode" class="website-lead-dialog__card">
        <header class="lead-dialog-head">
          <div class="lead-dialog-head__identity">
            <span class="lead-dialog-avatar">
              <v-icon size="19" color="#D4DCFF">
                {{ replyChannel === 'SMS' ? 'mdi-message-reply-text-outline' : 'mdi-email-arrow-right-outline' }}
              </v-icon>
            </span>
            <div v-if="bulkMode">
              <span>{{ $t('websiteLeads.contacts.bulk.title') }}</span>
              <h2>{{ $t('websiteLeads.contacts.bulk.recipients') }}: {{ bulkRecipients.length }}</h2>
              <p>{{ bulkPreviewNames }}</p>
            </div>
            <div v-else>
              <span>{{ $t('websiteLeads.reply.title') }}</span>
              <h2>{{ replyLead.name }}</h2>
              <p>{{ replyChannel === 'SMS' ? replyLead.phone : replyLead.email }}</p>
            </div>
          </div>
          <div class="lead-dialog-head__right">
            <button type="button" class="lead-dialog-close" :aria-label="$t('common.close')" @click="replyDialog = false">
              <v-icon size="20">mdi-close</v-icon>
            </button>
          </div>
        </header>

        <v-card-text class="lead-dialog-body">
          <div class="reply-row">
            <span class="reply-label">{{ $t('websiteLeads.reply.channelLabel') }}</span>
            <div class="reply-chips">
              <button
                type="button"
                :disabled="bulkMode && !bulkSmsRecipients.length"
                :class="{ 'is-active': replyChannel === 'SMS' }"
                @click="replyChannel = 'SMS'"
              >
                <v-icon size="14">mdi-message-text-outline</v-icon>SMS
              </button>
              <button
                type="button"
                :disabled="bulkMode ? !bulkEmailRecipients.length : !replyLead.email"
                :class="{ 'is-active': replyChannel === 'EMAIL' }"
                @click="replyChannel = 'EMAIL'"
              >
                <v-icon size="14">mdi-email-outline</v-icon>E-mail
              </button>
            </div>
          </div>

          <div v-if="bulkMode && bulkSkippedCount" class="bulk-skipped-note">
            <v-icon size="14">mdi-alert-circle-outline</v-icon>
            {{ $t(replyChannel === 'SMS' ? 'websiteLeads.contacts.bulk.skippedPhone' : 'websiteLeads.contacts.bulk.skippedEmail', { n: bulkSkippedCount }) }}
          </div>

          <div class="reply-row">
            <span class="reply-label">{{ $t('websiteLeads.reply.langLabel') }}</span>
            <div class="reply-chips">
              <button
                v-for="l in replyLangs"
                :key="l.value"
                type="button"
                :class="{ 'is-active': replyLang === l.value }"
                @click="setReplyLang(l.value)"
              >
                {{ l.label }}
              </button>
            </div>
          </div>

          <div v-if="replyChannel === 'EMAIL'" class="reply-field">
            <label>{{ $t('websiteLeads.reply.subjectLabel') }}</label>
            <input
              v-model="replySubject"
              type="text"
              class="reply-input"
              maxlength="200"
              :placeholder="$t('websiteLeads.reply.subjectPlaceholder')"
            />
          </div>

          <div class="reply-field">
            <label>{{ $t('websiteLeads.reply.templateLabel') }}</label>
            <div class="reply-template-bar">
              <select v-model="replyTemplateId" class="reply-input reply-select" @change="applyReplyTemplate">
                <option value="">{{ $t('websiteLeads.reply.templatePlaceholder') }}</option>
                <optgroup label="Wbudowane">
                  <option v-for="t in builtinReplyTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
                </optgroup>
                <optgroup v-if="replyTemplates.length" label="Zapisane">
                  <option v-for="t in replyTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
                </optgroup>
              </select>
              <button
                type="button"
                class="reply-template-btn"
                :title="$t('websiteLeads.reply.templateSave')"
                :disabled="!replyText.trim()"
                @click="saveReplyTemplate"
              >
                <v-icon size="16">mdi-content-save-plus-outline</v-icon>
              </button>
              <button
                type="button"
                class="reply-template-btn is-danger"
                :title="$t('websiteLeads.reply.templateDelete')"
                :disabled="!replyTemplateId"
                @click="deleteReplyTemplate"
              >
                <v-icon size="16">mdi-trash-can-outline</v-icon>
              </button>
            </div>
          </div>

          <div v-if="replyChannel === 'SMS'" class="reply-strip-row">
            <button
              type="button"
              class="reply-strip-toggle"
              :class="{ 'is-on': replyStripPolish }"
              @click="toggleStripPolish"
            >
              <v-icon size="15">{{ replyStripPolish ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
              {{ $t('sms.stripPolish') }}
            </button>
          </div>

          <div class="reply-field">
            <label>{{ $t('websiteLeads.reply.textLabel') }}</label>
            <textarea
              v-model="replyText"
              class="reply-textarea"
              rows="4"
              maxlength="5000"
              :placeholder="$t('websiteLeads.reply.textPlaceholder')"
              @input="replyTranslated = ''"
            ></textarea>
          </div>

          <div v-if="replyLang !== 'pl'" class="reply-translate-bar">
            <button
              type="button"
              class="lead-dialog-edit"
              :disabled="translatingReply || !replyText.trim()"
              @click="translateReply"
            >
              <v-progress-circular v-if="translatingReply" indeterminate size="13" width="2" />
              <v-icon v-else size="15">mdi-translate</v-icon>
              {{ $t('websiteLeads.reply.translateBtn') }} → {{ langName(replyLang) }}
            </button>
            <small v-if="!replyTranslated">{{ $t('websiteLeads.reply.needTranslate') }}</small>
          </div>

          <div v-if="replyLang !== 'pl' && replyTranslated" class="reply-field">
            <label>{{ $t('websiteLeads.reply.translatedLabel') }}</label>
            <textarea v-model="replyTranslated" class="reply-textarea" rows="4" maxlength="5000"></textarea>
          </div>

          <div
            v-if="replyChannel === 'SMS' && finalReplyText"
            class="reply-counter"
            :class="{ 'is-over': smsTooLong }"
          >
            <v-icon size="13">mdi-counter</v-icon>
            {{ finalReplyText.length }} zn. · ~{{ smsParts(finalReplyText) }} SMS
            <span v-if="smsNonGsmChars.length" class="reply-counter__unicode">
              · {{ $t('websiteLeads.reply.unicodeBecause') }}
              <code v-for="ch in smsNonGsmChars" :key="'ng-' + ch">{{ ch }}</code>
            </span>
            <b v-if="smsTooLong">{{ $t('websiteLeads.reply.tooLong') }}</b>
          </div>

          <div v-if="!bulkMode && replies.length" class="reply-history">
            <label>{{ $t('websiteLeads.reply.historyTitle') }}</label>
            <div class="lead-reply-list">
              <article v-for="r in replies" :key="'rd-' + r.id" class="lead-reply-item" :class="{ 'is-failed': !r.ok }">
                <header>
                  <v-icon size="14">{{ r.channel === 'SMS' ? 'mdi-message-text-outline' : 'mdi-email-outline' }}</v-icon>
                  <strong>{{ r.channel === 'SMS' ? 'SMS' : 'E-mail' }} → {{ r.sentTo }}</strong>
                  <span v-if="r.lang && r.lang !== 'pl'" class="lead-reply-lang">{{ langName(r.lang) }}</span>
                  <span v-if="!r.ok" class="lead-reply-failed">{{ $t('websiteLeads.reply.failed') }}</span>
                  <time>{{ formatDateTime(r.createdAt) }}</time>
                </header>
                <p>{{ r.sentText }}</p>
                <small v-if="r.sentBy" class="lead-reply-by">{{ r.sentBy }}</small>
              </article>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="lead-dialog-actions">
          <span></span>
          <button
            type="button"
            class="lead-dialog-status-action is-handled"
            :disabled="!canSendReply || sendingReply"
            @click="sendReply"
          >
            <v-progress-circular v-if="sendingReply" indeterminate size="14" width="2" />
            <v-icon v-else size="16">mdi-send-outline</v-icon>
            <template v-if="bulkMode && sendingReply">{{ bulkProgress }}/{{ bulkRecipients.length }}</template>
            <template v-else-if="bulkMode">{{ $t('websiteLeads.reply.send') }} ({{ bulkRecipients.length }})</template>
            <template v-else>{{ $t('websiteLeads.reply.send') }}</template>
          </button>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import Papa from 'papaparse';

// Ta sama mapa co w module SMS — polskie znaki wymuszają Unicode
// (limit ~402 zn. łącznie zamiast 918) i SMSAPI odrzuca dłuższe teksty
// błędem 11 "Message is too long".
const POLISH_MAP = { ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z', Ą: 'A', Ć: 'C', Ę: 'E', Ł: 'L', Ń: 'N', Ó: 'O', Ś: 'S', Ź: 'Z', Ż: 'Z' };
// Typograficzna interpunkcja (długi myślnik, „cudzysłowy", wielokropek…)
// też jest spoza GSM-7 — JEDEN taki znak przełącza cały SMS na Unicode,
// mimo że "polskich znaków nie ma".
const TYPO_MAP = { '—': '-', '–': '-', '−': '-', '„': '"', '“': '"', '”': '"', '‚': "'", '‘': "'", '’': "'", '«': '"', '»': '"', '…': '...', '\u00a0': ' ', '\u200b': '', '\ufeff': '' };
function stripPolishDiacritics(value) {
  return String(value || '')
    .replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, (ch) => POLISH_MAP[ch] || ch)
    .replace(/[—–−„“”‚‘’«»…\u00a0\u200b\ufeff]/g, (ch) => (ch in TYPO_MAP ? TYPO_MAP[ch] : ch));
}

export default {
  name: 'WebsiteLeadsPage',
  data() {
    return {
      loading: false,
      statsLoading: false,
      leads: [],
      allLeads: [],
      contactsLoading: false,
      contactsLoaded: false,
      selectedContacts: [],
      bulkMode: false,
      bulkProgress: 0,
      newCount: 0,
      stats: null,
      search: '',
      statusFilter: 'NEW',
      updatingId: null,
      deletingId: null,
      detailsDialog: false,
      selectedLead: null,
      translatingMessage: false,
      replies: [],
      replyDialog: false,
      replyLead: null,
      replyChannel: 'SMS',
      replyLang: 'pl',
      replyText: '',
      replyTranslated: '',
      replySubject: '',
      translatingReply: false,
      sendingReply: false,
      replyTemplates: [],
      // Wbudowane szablony odpowiedzi. {imie} = imię z leada.
      builtinReplyTemplates: [
        { id: 'builtin:no-contact', name: 'Brak kontaktu — odezwij się',
          sms: 'Dzień dobry {imie}, dziękujemy za zgłoszenie w BTP. Próbowaliśmy się skontaktować — oddzwoń: 52 516 11 12. Pozdrawiamy, BTPartner',
          email: 'Dzień dobry {imie},\n\ndziękujemy za zgłoszenie przez naszą stronę. Próbowaliśmy się z Tobą skontaktować, ale bez powodzenia. Prosimy o telefon: 52 516 11 12 (pon.–pt. 9:00–17:00) albo odpowiedź na tę wiadomość.\n\nPozdrawiamy,\nBTPartner' },
        { id: 'builtin:welcome', name: 'Powitanie po zgłoszeniu',
          sms: 'Dzień dobry {imie}, dziękujemy za zgłoszenie w BTP! Wkrótce oddzwonimy. Chcesz zacząć od razu? Zarejestruj się: btpapp.btpartner.pl/register. BTPartner',
          email: 'Dzień dobry {imie},\n\ndziękujemy za zainteresowanie współpracą z BTP! Skontaktujemy się wkrótce. Jeśli chcesz przyspieszyć — zarejestruj się online: btpapp.btpartner.pl/register\n\nPozdrawiamy,\nBTPartner' },
        { id: 'builtin:register', name: 'Zachęta do rejestracji',
          sms: 'Dzień dobry {imie}, dokończ rejestrację w BTP na btpapp.btpartner.pl/register — pomożemy z formalnościami i rozliczeniami. Pytania? 52 516 11 12. BTPartner',
          email: 'Dzień dobry {imie},\n\nzarejestruj się w BTP: btpapp.btpartner.pl/register — zajmiemy się formalnościami, rozliczeniami i (jeśli trzeba) autem z floty.\n\nW razie pytań: 52 516 11 12.\n\nPozdrawiamy,\nBTPartner' },
      ],
      replyTemplateId: '',
      replyStripPolish: true,
    };
  },
  computed: {
    isRefreshing() {
      return this.loading || this.statsLoading || this.contactsLoading;
    },
    isContactsView() {
      return this.statusFilter === 'CONTACTS';
    },
    // Baza kontaktów: wszystkie zgłoszenia zdeduplikowane po numerze telefonu
    // (a przy jego braku po e-mailu), z liczbą zgłoszeń i datą ostatniego.
    contacts() {
      const map = new Map();
      for (const lead of this.allLeads) {
        const phoneKey = String(lead.phone || '').replace(/\D/g, '');
        const emailKey = String(lead.email || '').trim().toLowerCase();
        const key = phoneKey || emailKey || 'lead-' + lead.id;
        const prev = map.get(key);
        if (!prev) {
          map.set(key, {
            key,
            name: lead.name,
            phone: lead.phone,
            email: lead.email || '',
            city: lead.city || '',
            leadsCount: 1,
            lastAt: lead.createdAt,
            lead,
          });
        } else {
          prev.leadsCount += 1;
          if (new Date(lead.createdAt) > new Date(prev.lastAt)) {
            prev.lastAt = lead.createdAt;
            prev.lead = lead;
            if (lead.name) prev.name = lead.name;
            if (lead.phone) prev.phone = lead.phone;
          }
          if (!prev.email && lead.email) prev.email = lead.email;
          if (!prev.city && lead.city) prev.city = lead.city;
        }
      }
      return [...map.values()];
    },
    contactsCount() {
      return this.contactsLoaded ? this.contacts.length : (this.stats && this.stats.total) || 0;
    },
    contactsHeaders() {
      return [
        { text: this.$t('websiteLeads.headers.name'), value: 'name', sortable: true, width: '260px' },
        { text: this.$t('websiteLeads.headers.phone'), value: 'phone', sortable: false, width: '180px' },
        { text: this.$t('websiteLeads.headers.email'), value: 'email', sortable: false, width: '230px' },
        { text: this.$t('websiteLeads.contacts.leadsHeader'), value: 'leadsCount', sortable: true, width: '170px' },
        { text: this.$t('websiteLeads.headers.actions'), value: 'actions', sortable: false, width: '210px', align: 'end' },
      ];
    },
    replyLangs() {
      return [
        { value: 'pl', label: this.$t('websiteLeads.reply.langPl') },
        { value: 'en', label: this.$t('websiteLeads.reply.langEn') },
        { value: 'uk', label: this.$t('websiteLeads.reply.langUk') },
        { value: 'ru', label: this.$t('websiteLeads.reply.langRu') },
        { value: 'ka', label: this.$t('websiteLeads.reply.langKa') },
      ];
    },
    // Tekst, który faktycznie wyjdzie: polski oryginał albo tłumaczenie.
    finalReplyText() {
      return this.replyLang === 'pl' ? this.replyText.trim() : this.replyTranslated.trim();
    },
    // Znaki spoza ASCII, które wymuszają kodowanie Unicode — pokazujemy je
    // w liczniku, żeby było jasne DLACZEGO limit spadł do ~400.
    smsNonGsmChars() {
      if (this.replyChannel !== 'SMS') return [];
      const seen = new Set();
      for (const ch of this.finalReplyText) {
        if (ch.charCodeAt(0) > 127) seen.add(ch);
      }
      return [...seen].slice(0, 8);
    },
    // Limit SMSAPI dla wiadomości łączonych: 918 zn. (GSM) / ~402 zn. (Unicode).
    // Powyżej wysyłka pada błędem 11 "Message is too long".
    smsTooLong() {
      if (this.replyChannel !== 'SMS' || !this.finalReplyText) return false;
      const unicode = this.smsNonGsmChars.length > 0;
      return this.finalReplyText.length > (unicode ? 402 : 918);
    },
    canSendReply() {
      if (this.bulkMode && !this.bulkRecipients.length) return false;
      return !!this.finalReplyText && !this.smsTooLong;
    },
    bulkSmsRecipients() {
      return this.selectedContacts.filter((c) => c.phone);
    },
    bulkEmailRecipients() {
      return this.selectedContacts.filter((c) => c.email);
    },
    bulkRecipients() {
      return this.replyChannel === 'SMS' ? this.bulkSmsRecipients : this.bulkEmailRecipients;
    },
    bulkSkippedCount() {
      return this.selectedContacts.length - this.bulkRecipients.length;
    },
    bulkPreviewNames() {
      const names = this.bulkRecipients.map((c) => c.name).filter(Boolean);
      const head = names.slice(0, 3).join(', ');
      return names.length > 3 ? head + ' +' + (names.length - 3) : head;
    },
    metricCards() {
      const stats = this.stats || {};
      return [
        {
          key: 'today',
          label: this.$t('websiteLeads.stats.today'),
          value: stats.today || 0,
          hint: this.$t('websiteLeads.stats.todayHint'),
          icon: 'mdi-calendar-today-outline',
          tone: 'blue',
        },
        {
          key: 'last7',
          label: this.$t('websiteLeads.stats.last7'),
          value: stats.last7 || 0,
          hint: this.$t('websiteLeads.stats.last7Hint'),
          icon: 'mdi-calendar-week-outline',
          tone: 'violet',
        },
        {
          key: 'last30',
          label: this.$t('websiteLeads.stats.last30'),
          value: stats.last30 || 0,
          hint: this.$t('websiteLeads.stats.last30Hint'),
          icon: 'mdi-chart-line',
          tone: 'amber',
        },
        {
          key: 'total',
          label: this.$t('websiteLeads.stats.total'),
          value: stats.total || 0,
          hint: this.$t('websiteLeads.stats.totalHint'),
          icon: 'mdi-account-multiple-check-outline',
          tone: 'green',
        },
      ];
    },
    statusSegments() {
      const byStatus = (this.stats && this.stats.byStatus) || {};
      return [
        {
          value: 'NEW',
          label: this.$t('websiteLeads.statusNew'),
          count: byStatus.NEW || 0,
          tone: 'new',
        },
        {
          value: 'IN_PROGRESS',
          label: this.$t('websiteLeads.statusInProgress'),
          count: byStatus.IN_PROGRESS || 0,
          tone: 'progress',
        },
        {
          value: 'HANDLED',
          label: this.$t('websiteLeads.statusHandled'),
          count: byStatus.HANDLED || 0,
          tone: 'handled',
        },
      ];
    },
    statusTabs() {
      const byStatus = (this.stats && this.stats.byStatus) || {};
      return [
        {
          value: 'NEW',
          label: this.$t('websiteLeads.filterNew'),
          count: byStatus.NEW || 0,
          icon: 'mdi-inbox-arrow-down-outline',
          tone: 'new',
        },
        {
          value: 'IN_PROGRESS',
          label: this.$t('websiteLeads.filterInProgress'),
          count: byStatus.IN_PROGRESS || 0,
          icon: 'mdi-progress-clock',
          tone: 'progress',
        },
        {
          value: 'HANDLED',
          label: this.$t('websiteLeads.filterHandled'),
          count: byStatus.HANDLED || 0,
          icon: 'mdi-check-circle-outline',
          tone: 'handled',
        },
        {
          value: 'ALL',
          label: this.$t('websiteLeads.filterAll'),
          count: (this.stats && this.stats.total) || 0,
          icon: 'mdi-view-grid-outline',
          tone: 'all',
        },
      ];
    },
    computedHeaders() {
      return [
        { text: this.$t('websiteLeads.headers.lead'), value: 'createdAt', sortable: true, width: '245px' },
        { text: this.$t('websiteLeads.headers.contact'), value: 'phone', sortable: false, width: '225px' },
        { text: this.$t('websiteLeads.headers.message'), value: 'message', sortable: false, width: '270px' },
        { text: this.$t('websiteLeads.headers.note'), value: 'note', sortable: false, width: '230px' },
        { text: this.$t('websiteLeads.headers.status'), value: 'status', sortable: false, width: '175px' },
        { text: this.$t('websiteLeads.headers.actions'), value: 'actions', sortable: false, width: '185px', align: 'end' },
      ];
    },
  },
  watch: {
    statusFilter(v) {
      if (v === 'CONTACTS') {
        this.fetchAllLeads();
      } else {
        this.fetchLeads();
      }
    },
    // Tryb "bez polskich znaków" czyści tekst na bieżąco (jak w module SMS).
    replyText(v) {
      if (this.replyStripPolish && this.replyChannel === 'SMS') {
        const s = stripPolishDiacritics(v);
        if (s !== v) this.replyText = s;
      }
    },
    replyTranslated(v) {
      if (this.replyStripPolish && this.replyChannel === 'SMS') {
        const s = stripPolishDiacritics(v);
        if (s !== v) this.replyTranslated = s;
      }
    },
    replyChannel(v) {
      if (v === 'SMS' && this.replyStripPolish) this.applyStripPolish();
    },
  },
  async created() {
    await Promise.all([this.fetchLeads(), this.fetchStats()]);
  },
  methods: {
    getConfig() {
      return { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
    },
    async refreshAll() {
      const jobs = [this.fetchLeads(), this.fetchStats()];
      if (this.contactsLoaded) jobs.push(this.fetchAllLeads());
      await Promise.all(jobs);
    },
    async fetchAllLeads() {
      this.contactsLoading = true;
      try {
        const res = await axios({ method: 'get', url: process.env.VUE_APP_ROOT_API + 'website-leads', ...this.getConfig() });
        this.allLeads = res.data?.data?.leads || [];
        this.contactsLoaded = true;
      } catch (e) {
        console.error('Failed to fetch contacts:', e);
      } finally {
        this.contactsLoading = false;
      }
    },
    exportContactsCsv() {
      const rows = this.contacts.map((c) => ({
        [this.$t('websiteLeads.headers.name')]: c.name,
        [this.$t('websiteLeads.headers.phone')]: c.phone,
        [this.$t('websiteLeads.headers.email')]: c.email,
        [this.$t('websiteLeads.headers.city')]: c.city,
        [this.$t('websiteLeads.contacts.leadsHeader')]: c.leadsCount,
        [this.$t('websiteLeads.contacts.lastLeadHeader')]: this.formatDateTime(c.lastAt),
      }));
      // BOM + średnik: żeby polski Excel otworzył plik bez krzaków i w kolumnach.
      const csv = '\ufeff' + Papa.unparse(rows, { delimiter: ';' });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'kontakty-zgloszenia-' + new Date().toISOString().slice(0, 10) + '.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    async fetchStats() {
      this.statsLoading = true;
      try {
        const res = await axios({ method: 'get', url: process.env.VUE_APP_ROOT_API + 'website-leads/stats', ...this.getConfig() });
        this.stats = res.data?.data || null;
      } catch (e) {
        console.error('Failed to fetch leads stats:', e);
      } finally {
        this.statsLoading = false;
      }
    },
    statusBarWidth(count) {
      const total = Math.max(0, Number((this.stats && this.stats.total) || 0));
      if (!total || !count) return '0%';
      return Math.max(5, Math.round((Number(count) / total) * 100)) + '%';
    },
    // Wysokość słupka: skala względem maksimum z 30 dni.
    barHeight(count) {
      const daily = (this.stats && this.stats.daily) || [];
      const max = Math.max(1, ...daily.map((d) => Number(d.count) || 0));
      return Number(count) === 0 ? '3px' : Math.max(7, Math.round((Number(count) / max) * 72)) + 'px';
    },
    formatDay(day) {
      if (!day || !day.includes('-')) return '—';
      const [, m, d] = day.split('-');
      return `${d}.${m}`;
    },
    statusTone(status) {
      return { NEW: 'new', IN_PROGRESS: 'progress', HANDLED: 'handled' }[status] || 'neutral';
    },
    statusLabel(status) {
      if (status === 'IN_PROGRESS') return this.$t('websiteLeads.statusInProgress');
      return status === 'NEW' ? this.$t('websiteLeads.statusNew') : this.$t('websiteLeads.statusHandled');
    },
    leadInitials(name) {
      const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
      return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('') || '?';
    },
    noteMeta(item) {
      return [item.noteBy, this.formatDateTime(item.noteAt)].filter(Boolean).join(' · ');
    },
    sourceLabel(source) {
      const labels = {
        'www-kontakt': 'btpartner.pl',
        'www': 'btpartner.pl',
      };
      return labels[source] || source || 'btpartner.pl';
    },
    nextStatus(item) {
      if (item.status === 'NEW') return 'IN_PROGRESS';
      if (item.status === 'IN_PROGRESS') return 'HANDLED';
      return 'NEW';
    },
    primaryActionLabel(item) {
      if (item.status === 'NEW') return this.$t('websiteLeads.markInProgress');
      if (item.status === 'IN_PROGRESS') return this.$t('websiteLeads.markHandled');
      return this.$t('websiteLeads.markNew');
    },
    primaryActionIcon(item) {
      if (item.status === 'NEW') return 'mdi-progress-clock';
      if (item.status === 'IN_PROGRESS') return 'mdi-check';
      return 'mdi-undo';
    },
    primaryActionTone(item) {
      if (item.status === 'NEW') return 'progress';
      if (item.status === 'IN_PROGRESS') return 'handled';
      return 'neutral';
    },
    availableStatuses(item) {
      return [
        {
          value: 'NEW',
          label: this.$t('websiteLeads.statusNew'),
          icon: 'mdi-inbox-arrow-down-outline',
          color: '#8EA7FF',
          tone: 'new',
        },
        {
          value: 'IN_PROGRESS',
          label: this.$t('websiteLeads.statusInProgress'),
          icon: 'mdi-progress-clock',
          color: '#FFBE63',
          tone: 'progress',
        },
        {
          value: 'HANDLED',
          label: this.$t('websiteLeads.statusHandled'),
          icon: 'mdi-check-circle-outline',
          color: '#66D99A',
          tone: 'handled',
        },
      ].filter((status) => status.value !== item.status);
    },
    openLead(item) {
      this.selectedLead = item;
      this.detailsDialog = true;
      this.fetchReplies(item.id);
    },
    replyCount(item) {
      return (item._count && item._count.replies) || 0;
    },
    langName(code) {
      const key = { pl: 'langPl', en: 'langEn', uk: 'langUk', ru: 'langRu', ka: 'langKa' }[code];
      return key ? this.$t('websiteLeads.reply.' + key) : code;
    },
    // Szacunek segmentów SMS: polskie znaki wymuszają Unicode (70/67 zn.).
    smsParts(text) {
      const unicode = [...text].some((ch) => ch.charCodeAt(0) > 127);
      const len = text.length;
      if (unicode) return len <= 70 ? 1 : Math.ceil(len / 67);
      return len <= 160 ? 1 : Math.ceil(len / 153);
    },
    async fetchReplies(leadId) {
      this.replies = [];
      try {
        const res = await axios({ method: 'get', url: process.env.VUE_APP_ROOT_API + 'website-leads/' + leadId + '/replies', ...this.getConfig() });
        this.replies = res.data?.data || [];
      } catch (e) {
        console.error('Failed to fetch lead replies:', e);
      }
    },
    async translateLeadMessage() {
      if (!this.selectedLead) return;
      this.translatingMessage = true;
      try {
        const res = await axios({ method: 'post', url: process.env.VUE_APP_ROOT_API + 'website-leads/' + this.selectedLead.id + '/translate-message', ...this.getConfig() });
        const d = res.data?.data || {};
        this.selectedLead = { ...this.selectedLead, messagePl: d.messagePl, messageLang: d.messageLang };
      } catch (e) {
        console.error(e);
        Swal.fire({ icon: 'error', title: this.$t('common.error'), text: e.response?.data?.message || String(e) });
      } finally {
        this.translatingMessage = false;
      }
    },
    openReply(item, channel) {
      this.bulkMode = false;
      this.replyLead = item;
      this.replyChannel = channel === 'EMAIL' && item.email ? 'EMAIL' : 'SMS';
      this.replyLang = 'pl';
      this.replyText = '';
      this.replyTranslated = '';
      this.replySubject = '';
      this.replyTemplateId = '';
      this.replyStripPolish = true;
      this.replyDialog = true;
      this.fetchReplyTemplates();
      this.fetchReplies(item.id);
    },
    openBulkReply(channel) {
      this.bulkMode = true;
      this.replyLead = null;
      this.replies = [];
      this.replyChannel = channel === 'EMAIL' && this.bulkEmailRecipients.length ? 'EMAIL' : 'SMS';
      this.replyLang = 'pl';
      this.replyText = '';
      this.replyTranslated = '';
      this.replySubject = '';
      this.replyTemplateId = '';
      this.replyStripPolish = true;
      this.bulkProgress = 0;
      this.replyDialog = true;
      this.fetchReplyTemplates();
    },
    // Wysyłka zbiorcza: po kolei do każdego zaznaczonego kontaktu (na jego
    // ostatnie zgłoszenie), żeby każda wiadomość trafiła do historii osoby.
    async sendBulkReply() {
      const recipients = this.bulkRecipients;
      if (!recipients.length || !this.canSendReply) return;
      this.sendingReply = true;
      this.bulkProgress = 0;
      const failed = [];
      for (const c of recipients) {
        try {
          await axios({
            method: 'post',
            url: process.env.VUE_APP_ROOT_API + 'website-leads/' + c.lead.id + '/reply',
            data: {
              channel: this.replyChannel,
              text: this.finalReplyText,
              originalText: this.replyLang !== 'pl' ? this.replyText.trim() : undefined,
              lang: this.replyLang,
              subject: this.replyChannel === 'EMAIL' ? this.replySubject.trim() || undefined : undefined,
            },
            ...this.getConfig(),
          });
        } catch (e) {
          console.error('Bulk reply failed for', c.name, e);
          failed.push(c.name || c.phone || c.email);
        }
        this.bulkProgress += 1;
      }
      this.sendingReply = false;
      this.replyDialog = false;
      if (failed.length) {
        Swal.fire({
          icon: 'warning',
          title: this.$t('websiteLeads.contacts.bulk.partial', { n: recipients.length - failed.length, total: recipients.length }),
          text: this.$t('websiteLeads.contacts.bulk.failedList') + ' ' + failed.join(', '),
        });
      } else {
        Swal.fire({
          icon: 'success',
          title: this.$t('websiteLeads.contacts.bulk.sent', { n: recipients.length }),
          timer: 2000,
          showConfirmButton: false,
        });
        this.selectedContacts = [];
      }
      await this.refreshAll();
      this.$root.$emit('website-leads-updated');
    },
    toggleStripPolish() {
      this.replyStripPolish = !this.replyStripPolish;
      if (this.replyStripPolish) this.applyStripPolish();
    },
    applyStripPolish() {
      this.replyText = stripPolishDiacritics(this.replyText);
      if (this.replyTranslated) this.replyTranslated = stripPolishDiacritics(this.replyTranslated);
    },
    async fetchReplyTemplates() {
      try {
        const res = await axios({ method: 'get', url: process.env.VUE_APP_ROOT_API + 'website-leads/reply-templates', ...this.getConfig() });
        this.replyTemplates = res.data?.data || [];
      } catch (e) {
        console.error('Failed to fetch reply templates:', e);
      }
    },
    applyReplyTemplate() {
      const id = this.replyTemplateId;
      if (String(id).startsWith('builtin:')) {
        const tpl = this.builtinReplyTemplates.find((x) => x.id === id);
        if (!tpl) return;
        const lead = this.replyLead || {};
        const imie = String(lead.name || '').trim().split(/\s+/)[0] || '';
        const tresc = this.replyChannel === 'SMS' ? tpl.sms : tpl.email;
        this.replyText = tresc.replace(/\{imie\}/g, imie).replace(/ {2,}/g, ' ').trim();
        this.replyTranslated = '';
        return;
      }
      const t = this.replyTemplates.find((x) => x.id === id);
      if (!t) return;
      this.replyText = t.content;
      this.replyTranslated = '';
    },
    async saveReplyTemplate() {
      const { value: name, isConfirmed } = await Swal.fire({
        title: this.$t('websiteLeads.reply.templateSaveTitle'),
        input: 'text',
        inputPlaceholder: this.$t('websiteLeads.reply.templateSaveHint'),
        inputAttributes: { maxlength: 120 },
        showCancelButton: true,
        confirmButtonText: this.$t('common.save'),
        cancelButtonText: this.$t('common.cancel'),
      });
      if (!isConfirmed || !name || !name.trim()) return;
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'website-leads/reply-templates',
          data: { name: name.trim(), content: this.replyText.trim() },
          ...this.getConfig(),
        });
        await this.fetchReplyTemplates();
        this.replyTemplateId = res.data?.data?.id || '';
        Swal.fire({ icon: 'success', title: this.$t('websiteLeads.reply.templateSaved'), timer: 1400, showConfirmButton: false });
      } catch (e) {
        console.error(e);
        Swal.fire({ icon: 'error', title: this.$t('common.error'), text: e.response?.data?.message || String(e) });
      }
    },
    async deleteReplyTemplate() {
      if (!this.replyTemplateId) return;
      const res = await Swal.fire({
        icon: 'warning',
        title: this.$t('websiteLeads.reply.templateDeleteTitle'),
        text: this.$t('websiteLeads.reply.templateDeleteText'),
        showCancelButton: true,
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.cancel'),
        confirmButtonColor: '#d33',
      });
      if (!res.isConfirmed) return;
      try {
        await axios({ method: 'delete', url: process.env.VUE_APP_ROOT_API + 'website-leads/reply-templates/' + this.replyTemplateId, ...this.getConfig() });
        this.replyTemplateId = '';
        await this.fetchReplyTemplates();
      } catch (e) {
        console.error(e);
        Swal.fire({ icon: 'error', title: this.$t('common.error'), text: e.response?.data?.message || String(e) });
      }
    },
    setReplyLang(lang) {
      this.replyLang = lang;
      this.replyTranslated = '';
    },
    async translateReply() {
      this.translatingReply = true;
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'website-leads/translate',
          data: { text: this.replyText.trim(), targetLang: this.replyLang },
          ...this.getConfig(),
        });
        this.replyTranslated = res.data?.data?.text || '';
      } catch (e) {
        console.error(e);
        Swal.fire({ icon: 'error', title: this.$t('common.error'), text: e.response?.data?.message || String(e) });
      } finally {
        this.translatingReply = false;
      }
    },
    async sendReply() {
      if (this.bulkMode) return this.sendBulkReply();
      if (!this.replyLead || !this.canSendReply) return;
      this.sendingReply = true;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'website-leads/' + this.replyLead.id + '/reply',
          data: {
            channel: this.replyChannel,
            text: this.finalReplyText,
            originalText: this.replyLang !== 'pl' ? this.replyText.trim() : undefined,
            lang: this.replyLang,
            subject: this.replyChannel === 'EMAIL' ? this.replySubject.trim() || undefined : undefined,
          },
          ...this.getConfig(),
        });
        this.replyDialog = false;
        Swal.fire({ icon: 'success', title: this.$t('websiteLeads.reply.sent'), timer: 1600, showConfirmButton: false });
        if (this.selectedLead && this.selectedLead.id === this.replyLead.id) {
          this.fetchReplies(this.replyLead.id);
        }
        await this.refreshAll();
        this.$root.$emit('website-leads-updated');
      } catch (e) {
        console.error(e);
        Swal.fire({ icon: 'error', title: this.$t('common.error'), text: e.response?.data?.message || String(e) });
      } finally {
        this.sendingReply = false;
      }
    },
    async fetchLeads() {
      this.loading = true;
      try {
        const statusParam = this.statusFilter === 'ALL' || this.statusFilter === 'CONTACTS' ? '' : '?status=' + this.statusFilter;
        const res = await axios({ method: 'get', url: process.env.VUE_APP_ROOT_API + 'website-leads' + statusParam, ...this.getConfig() });
        this.leads = res.data?.data?.leads || [];
        this.newCount = res.data?.data?.newCount || 0;
        if (this.selectedLead) {
          const freshLead = this.leads.find((lead) => lead.id === this.selectedLead.id);
          if (freshLead) {
            this.selectedLead = freshLead;
          } else {
            this.detailsDialog = false;
            this.selectedLead = null;
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async setStatus(item, status) {
      this.updatingId = item.id;
      try {
        await axios({ method: 'patch', url: process.env.VUE_APP_ROOT_API + 'website-leads/' + item.id + '/status', data: { status }, ...this.getConfig() });
        await Promise.all([this.fetchLeads(), this.fetchStats()]);
        this.$root.$emit('website-leads-updated'); // odśwież licznik w menu od razu
      } catch (e) {
        console.error(e);
        Swal.fire({ icon: 'error', title: this.$t('common.error'), text: e.response?.data?.message || String(e) });
      } finally {
        this.updatingId = null;
      }
    },
    async editNote(item) {
      const { value: note, isConfirmed } = await Swal.fire({
        title: this.$t('websiteLeads.noteTitle'),
        input: 'textarea',
        inputValue: item.note || '',
        inputPlaceholder: this.$t('websiteLeads.noteHint'),
        inputAttributes: { maxlength: 2000 },
        showCancelButton: true,
        confirmButtonText: this.$t('common.save'),
        cancelButtonText: this.$t('common.cancel'),
      });
      if (!isConfirmed) return;
      this.updatingId = item.id;
      try {
        await axios({ method: 'patch', url: process.env.VUE_APP_ROOT_API + 'website-leads/' + item.id + '/note', data: { note: note || '' }, ...this.getConfig() });
        await this.fetchLeads();
      } catch (e) {
        console.error(e);
        Swal.fire({ icon: 'error', title: this.$t('common.error'), text: e.response?.data?.message || String(e) });
      } finally {
        this.updatingId = null;
      }
    },
    async deleteLead(item) {
      const res = await Swal.fire({
        icon: 'warning',
        title: this.$t('websiteLeads.deleteTitle'),
        text: this.$t('websiteLeads.deleteText'),
        showCancelButton: true,
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.cancel'),
        confirmButtonColor: '#d33',
      });
      if (!res.isConfirmed) return;
      this.deletingId = item.id;
      try {
        await axios({ method: 'delete', url: process.env.VUE_APP_ROOT_API + 'website-leads/' + item.id, ...this.getConfig() });
        if (this.selectedLead && this.selectedLead.id === item.id) {
          this.detailsDialog = false;
          this.selectedLead = null;
        }
        await this.refreshAll();
        this.$root.$emit('website-leads-updated'); // odśwież licznik w menu od razu
      } catch (e) {
        console.error(e);
        Swal.fire({ icon: 'error', title: this.$t('common.error'), text: e.response?.data?.message || String(e) });
      } finally {
        this.deletingId = null;
      }
    },
    formatDateTime(d) {
      if (!d) return '—';
      return new Date(d).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });
    },
  },
};
</script>

<style scoped>
.website-leads-page {
  --leads-bg: #171B26;
  --leads-surface: #232838;
  --leads-surface-soft: #1D2231;
  --leads-surface-hover: #292F42;
  --leads-border: rgba(255, 255, 255, 0.075);
  --leads-text: #F5F7FC;
  --leads-muted: #AEB8CA;
  --leads-blue: #6688FF;
  --leads-violet: #A68DFF;
  --leads-amber: #FFBD66;
  --leads-green: #50D890;
  min-height: calc(100vh - 56px);
  margin: -24px;
  padding: 24px clamp(16px, 2.2vw, 34px) 52px;
  overflow: hidden;
  background:
    radial-gradient(circle at 6% -4%, rgba(84, 120, 255, 0.17), transparent 28%),
    radial-gradient(circle at 96% 7%, rgba(166, 141, 255, 0.09), transparent 24%),
    var(--leads-bg);
  color: var(--leads-text);
}
.website-leads-page * { box-sizing: border-box; }
.leads-hero,
.leads-metrics,
.leads-insights,
.leads-workspace {
  width: 100%;
  max-width: 1680px;
  margin-right: auto;
  margin-left: auto;
}
.website-leads-page button { font-family: inherit; }

/* Hero */
.leads-hero {
  position: relative;
  min-height: 132px;
  padding: 22px;
  overflow: hidden;
  border: 1px solid rgba(126, 151, 255, 0.16);
  border-radius: 22px;
  background: linear-gradient(128deg, rgba(38, 46, 70, 0.98), rgba(31, 36, 52, 0.96));
  box-shadow: 0 28px 60px -48px rgba(0, 0, 0, 0.95);
}
.leads-hero__glow {
  position: absolute;
  top: -110px;
  right: 5%;
  width: 330px;
  height: 260px;
  border-radius: 50%;
  background: rgba(84, 120, 255, 0.17);
  filter: blur(70px);
  pointer-events: none;
}
.leads-hero::after {
  position: absolute;
  right: -34px;
  bottom: -86px;
  width: 260px;
  height: 210px;
  border: 1px solid rgba(126, 151, 255, 0.08);
  border-radius: 50%;
  content: "";
}
.leads-hero__content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}
.leads-identity {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 15px;
}
.leads-identity__icon {
  width: 54px;
  height: 54px;
  flex: 0 0 54px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(142, 167, 255, 0.3);
  border-radius: 17px;
  background: linear-gradient(145deg, #4168FF, #755EF0);
  box-shadow: 0 18px 34px -18px rgba(80, 107, 255, 0.88);
}
.leads-identity__icon >>> .v-icon { color: #FFF !important; }
.leads-eyebrow,
.leads-section-eyebrow {
  color: #8EA7FF;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.105em;
  text-transform: uppercase;
}
.leads-identity h1 {
  margin: 4px 0 0;
  color: #FFF;
  font-size: clamp(25px, 2.4vw, 32px);
  font-weight: 900;
  letter-spacing: -0.045em;
  line-height: 1.05;
}
.leads-identity p {
  max-width: 690px;
  margin: 7px 0 0;
  color: var(--leads-muted);
  font-size: 13.5px;
  line-height: 1.45;
}
.leads-hero__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
}
.leads-inbox-state {
  min-width: 140px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 12px;
  border: 1px solid var(--leads-border);
  border-radius: 13px;
  background: rgba(24, 29, 43, 0.65);
}
.leads-inbox-state__dot {
  width: 8px;
  height: 8px;
  flex: 0 0 8px;
  border-radius: 50%;
  background: #738097;
  box-shadow: 0 0 0 4px rgba(115, 128, 151, 0.1);
}
.leads-inbox-state.has-new {
  border-color: rgba(102, 136, 255, 0.2);
  background: rgba(102, 136, 255, 0.08);
}
.leads-inbox-state.has-new .leads-inbox-state__dot {
  background: #7F9CFF;
  box-shadow: 0 0 0 4px rgba(102, 136, 255, 0.13);
  animation: lead-pulse 2s ease-in-out infinite;
}
.leads-inbox-state small {
  display: block;
  color: #77839A;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.leads-inbox-state strong {
  display: block;
  margin-top: 1px;
  color: #EBEFF7;
  font-size: 13px;
  font-weight: 850;
  font-variant-numeric: tabular-nums;
}
.leads-refresh {
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.055);
  color: #E8ECF4;
  font-size: 12.5px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.16s ease, border-color 0.16s ease;
}
.leads-refresh:hover:not(:disabled) {
  border-color: rgba(255, 255, 255, 0.19);
  background: rgba(255, 255, 255, 0.085);
}
.leads-refresh:disabled { opacity: 0.62; cursor: wait; }
.leads-refresh >>> .v-icon { color: inherit !important; }
.leads-refresh .is-spinning { animation: lead-spin 0.8s linear infinite; }

/* Karty statystyk */
.leads-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}
.leads-metric {
  --metric-color: #6688FF;
  --metric-rgb: 102, 136, 255;
  position: relative;
  min-width: 0;
  min-height: 96px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  overflow: hidden;
  border: 1px solid var(--leads-border);
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(35, 40, 56, 0.98), rgba(30, 35, 50, 0.98));
}
.leads-metric::after {
  position: absolute;
  top: 16px;
  bottom: 16px;
  left: 0;
  width: 2px;
  border-radius: 0 3px 3px 0;
  background: var(--metric-color);
  content: "";
}
.leads-metric--violet { --metric-color: var(--leads-violet); --metric-rgb: 166, 141, 255; }
.leads-metric--amber { --metric-color: var(--leads-amber); --metric-rgb: 255, 189, 102; }
.leads-metric--green { --metric-color: var(--leads-green); --metric-rgb: 80, 216, 144; }
.leads-metric__icon {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: rgba(var(--metric-rgb), 0.11);
}
.leads-metric__icon >>> .v-icon { color: var(--metric-color) !important; }
.leads-metric__copy {
  min-width: 0;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: end;
  column-gap: 10px;
}
.leads-metric__copy small {
  color: #8B96AA;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.leads-metric__copy strong {
  grid-row: span 2;
  color: #FFF;
  font-size: 27px;
  font-weight: 900;
  letter-spacing: -0.04em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.leads-metric__copy span {
  margin-top: 4px;
  overflow: hidden;
  color: #68758C;
  font-size: 11.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.leads-metrics--loading { min-height: 96px; }
.leads-metric-skeleton {
  min-height: 96px;
  border: 1px solid rgba(255, 255, 255, 0.045);
  border-radius: 16px;
  background: linear-gradient(100deg, #202533 25%, #262C3D 40%, #202533 60%);
  background-size: 200% 100%;
  animation: lead-shimmer 1.5s infinite;
}

/* Statusy i aktywność */
.leads-insights {
  display: grid;
  grid-template-columns: minmax(320px, 0.78fr) minmax(460px, 1.22fr);
  gap: 10px;
  margin-top: 10px;
}
.leads-insight-card {
  min-width: 0;
  min-height: 158px;
  padding: 15px;
  border: 1px solid var(--leads-border);
  border-radius: 17px;
  background: rgba(31, 36, 52, 0.94);
}
.leads-card-head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.leads-card-head__icon {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background: rgba(102, 136, 255, 0.11);
}
.leads-card-head__icon >>> .v-icon { color: #83A0FF !important; }
.leads-card-head__icon--violet { background: rgba(166, 141, 255, 0.11); }
.leads-card-head__icon--violet >>> .v-icon { color: #B8A6FF !important; }
.leads-card-head > div { min-width: 0; }
.leads-card-head h2 { margin: 0; color: #F4F6FA; font-size: 13.5px; font-weight: 850; }
.leads-card-head p { margin: 2px 0 0; color: #717D93; font-size: 11.5px; line-height: 1.35; }
.leads-status-list { display: grid; gap: 7px; margin-top: 13px; }
.leads-status-row {
  --status-color: #7F9CFF;
  width: 100%;
  min-height: 24px;
  display: grid;
  grid-template-columns: 7px minmax(80px, 0.65fr) minmax(90px, 1fr) 28px;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #929DB0;
  font-size: 11.5px;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
}
.leads-status-row:hover,
.leads-status-row.is-active { color: #DFE4EE; }
.leads-status-row:hover { background: rgba(255, 255, 255, 0.025); }
.leads-status-row__dot { width: 7px; height: 7px; border-radius: 50%; background: var(--status-color); }
.leads-status-row__label { text-align: left; }
.leads-status-row__track {
  height: 5px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.055);
}
.leads-status-row__track i {
  height: 100%;
  display: block;
  border-radius: inherit;
  background: var(--status-color);
  opacity: 0.78;
  transition: width 0.25s ease;
}
.leads-status-row strong { color: #E2E7F0; font-size: 12px; text-align: right; font-variant-numeric: tabular-nums; }
.leads-status-row--progress { --status-color: #FFBE63; }
.leads-status-row--handled { --status-color: #62D995; }
.leads-chart {
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 15px;
  border: 1px solid var(--leads-border);
  border-radius: 17px;
  background: rgba(31, 36, 52, 0.94);
}
.leads-chart__summary {
  min-width: 54px;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: auto;
}
.leads-chart__summary small { color: #6F7B91; font-size: 10px; font-weight: 750; letter-spacing: 0.05em; text-transform: uppercase; }
.leads-chart__summary strong { margin-top: 1px; color: #EDEFFD; font-size: 16px; line-height: 1; font-variant-numeric: tabular-nums; }
.leads-chart-bars {
  height: 76px;
  display: flex;
  align-items: flex-end;
  gap: 4px;
  margin-top: 10px;
  padding: 0 2px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px);
  background-size: 100% 24px;
}
.leads-bar-wrap {
  height: 100%;
  flex: 1;
  display: flex;
  align-items: flex-end;
  cursor: help;
}
.leads-bar {
  width: 100%;
  min-width: 3px;
  border-radius: 4px 4px 1px 1px;
  background: linear-gradient(180deg, #9B8AFF, #5F7EFF);
  box-shadow: 0 -8px 18px -13px rgba(126, 151, 255, 0.9);
  transition: height 0.2s ease, opacity 0.15s ease;
}
.leads-bar-wrap:hover .leads-bar { opacity: 0.72; }
.leads-bar--zero { background: #343B4C; box-shadow: none; }
.leads-chart-axis { display: flex; justify-content: space-between; margin-top: 5px; color: #667289; font-size: 10.5px; font-weight: 650; }
.leads-chart-empty { min-height: 82px; display: flex; align-items: center; justify-content: center; gap: 8px; color: #68748A; font-size: 12px; }
.leads-chart-empty >>> .v-icon { color: #59667D !important; }
</style>

<style scoped>
/* Lista i filtry */
.leads-workspace {
  margin-top: 12px;
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 20px;
  background: rgba(35, 40, 56, 0.96);
  box-shadow: 0 30px 58px -50px rgba(0, 0, 0, 0.95);
}
.leads-workspace__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
}
.leads-workspace__header h2 {
  margin: 3px 0 0;
  color: #FFF;
  font-size: 18px;
  font-weight: 900;
  letter-spacing: -0.03em;
}
.leads-workspace__header p { max-width: 650px; margin: 4px 0 0; color: #7D899F; font-size: 12px; }
.leads-search { width: min(390px, 100%); flex: 0 1 390px; }
.website-leads-page >>> .leads-search .v-input__slot {
  min-height: 40px !important;
  margin: 0;
  border-radius: 12px !important;
  background: #1B202E !important;
}
.website-leads-page >>> .leads-search .v-input__slot fieldset { border-color: rgba(255, 255, 255, 0.075) !important; }
.website-leads-page >>> .leads-search.v-input--is-focused .v-input__slot fieldset { border-color: rgba(102, 136, 255, 0.55) !important; }
.website-leads-page >>> .leads-search input { color: #E6EAF2 !important; font-size: 12.5px; }
.website-leads-page >>> .leads-search input::placeholder { color: #6F7B91 !important; opacity: 1; }
.website-leads-page >>> .leads-search .v-icon { color: #748097 !important; }
.leads-tabs {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 15px;
  padding: 5px;
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.055);
  border-radius: 13px;
  background: #1B202E;
  scrollbar-width: none;
}
.leads-tabs::-webkit-scrollbar { display: none; }
.leads-tab {
  --tab-rgb: 166, 141, 255;
  --tab-color: #C1B1FF;
  min-height: 35px;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 11px;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  color: #7D899F;
  font-size: 12px;
  font-weight: 750;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;
}
.leads-tab >>> .v-icon { color: inherit !important; }
.leads-tab small {
  min-width: 20px;
  padding: 2px 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.055);
  color: #8A95AA;
  font-size: 10.5px;
  font-weight: 850;
  line-height: 1.3;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.leads-tab:hover { color: #C8D0DF; background: rgba(255, 255, 255, 0.035); }
.leads-tab.is-active {
  border-color: rgba(var(--tab-rgb), 0.2);
  background: rgba(var(--tab-rgb), 0.115);
  color: var(--tab-color);
}
.leads-tab.is-active small { background: rgba(var(--tab-rgb), 0.16); color: var(--tab-color); }
.leads-tab--new { --tab-rgb: 102, 136, 255; --tab-color: #9AB0FF; }
.leads-tab--progress { --tab-rgb: 255, 189, 102; --tab-color: #FFD08F; }
.leads-tab--handled { --tab-rgb: 80, 216, 144; --tab-color: #82E7AE; }
.leads-tab--contacts { --tab-rgb: 166, 141, 255; --tab-color: #C6B4FF; margin-left: auto; }
.leads-results-bar {
  min-height: 34px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 3px;
  color: #728097;
  font-size: 11.5px;
}
.leads-results-bar > span { display: inline-flex; align-items: center; gap: 5px; }
.leads-results-bar >>> .v-icon { color: #66748C !important; }
.leads-results-bar strong { color: #D8DEE9; font-variant-numeric: tabular-nums; }
.leads-results-bar__search { max-width: 45%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.leads-export { min-height: 32px; margin-left: auto; padding: 5px 11px; font-size: 11px; }

/* Baza kontaktów (zakładka Kontakty) */
.contact-meta { display: inline-flex; align-items: center; gap: 8px; }
.contact-meta time { color: #8B96AB; font-size: 11px; font-variant-numeric: tabular-nums; }
.contact-count {
  min-width: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border: 1px solid rgba(166, 141, 255, 0.24);
  border-radius: 999px;
  background: rgba(166, 141, 255, 0.1);
  color: #C6B4FF;
  font-size: 11.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.contact-actions { justify-content: flex-end; }
.contact-actions .lead-primary-action { min-width: 0; }
.contact-actions .lead-primary-action:disabled { opacity: 0.4; cursor: not-allowed; }

/* Pasek wysyłki zbiorczej nad tabelą kontaktów */
.contacts-bulk-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 10px;
  padding: 10px 14px;
  border: 1px solid rgba(166, 141, 255, 0.24);
  border-radius: 13px;
  background: rgba(166, 141, 255, 0.08);
}
.contacts-bulk-bar__info {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 4px;
  color: #C6B4FF;
  font-size: 12px;
  font-weight: 600;
}
.contacts-bulk-bar__info >>> .v-icon { color: #C6B4FF !important; }
.contacts-bulk-bar__info strong { font-variant-numeric: tabular-nums; }
.contacts-bulk-bar .lead-primary-action:disabled { opacity: 0.4; cursor: not-allowed; }
.contacts-bulk-clear {
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  padding: 6px 10px;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  color: #8B96AB;
  font-size: 10.5px;
  cursor: pointer;
}
.contacts-bulk-clear:hover { color: #C8D0DF; background: rgba(255, 255, 255, 0.05); }
.contacts-bulk-clear >>> .v-icon { color: inherit !important; }

/* Checkboxy zaznaczania w ciemnej tabeli */
.website-leads-page >>> .leads-table .v-simple-checkbox .v-icon { color: #66748C !important; }
.website-leads-page >>> .leads-table .v-simple-checkbox .mdi-checkbox-marked,
.website-leads-page >>> .leads-table .v-simple-checkbox .mdi-minus-box { color: #A68DFF !important; }

.bulk-skipped-note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  padding: 8px 11px;
  border: 1px solid rgba(255, 189, 102, 0.22);
  border-radius: 10px;
  background: rgba(255, 189, 102, 0.07);
  color: #FFD08F;
  font-size: 11.5px;
}
.bulk-skipped-note >>> .v-icon { color: #FFD08F !important; }

/* Tabela */
.leads-table-shell {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 15px;
  background: #1D2231;
}
.website-leads-page >>> .leads-table { background: transparent !important; color: #E8ECF4 !important; }
.website-leads-page >>> .leads-table .v-data-table__wrapper { overflow-x: auto; }
.website-leads-page >>> .leads-table thead tr th {
  height: 42px;
  padding: 0 13px;
  background: #1D2231 !important;
  color: #748097 !important;
  font-size: 11px !important;
  font-weight: 850 !important;
  letter-spacing: 0.065em;
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
}
.website-leads-page >>> .leads-table tbody tr {
  background: #232838 !important;
  cursor: pointer;
  transition: background 0.15s ease;
}
.website-leads-page >>> .leads-table tbody tr:hover { background: #292F42 !important; }
.website-leads-page >>> .leads-table tbody tr td {
  height: 72px;
  padding: 9px 13px;
  color: #DCE1EB !important;
  font-size: 12px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
}
.website-leads-page >>> .leads-table .v-data-table__progress th { height: auto; padding: 0; }
.website-leads-page >>> .leads-table .v-progress-linear__background { background: rgba(102, 136, 255, 0.16) !important; }
.website-leads-page >>> .leads-table .v-progress-linear__determinate,
.website-leads-page >>> .leads-table .v-progress-linear__indeterminate { background: #6688FF !important; }
.website-leads-page >>> .leads-table .v-data-footer {
  min-height: 49px;
  background: #1D2231 !important;
  color: #8490A5 !important;
  border-top: 1px solid rgba(255, 255, 255, 0.055) !important;
}
.website-leads-page >>> .leads-table .v-data-footer .v-btn .v-icon,
.website-leads-page >>> .leads-table .v-data-footer__select .v-select__selection { color: #B3BDCF !important; }
.website-leads-page >>> .leads-table .v-data-footer__select,
.website-leads-page >>> .leads-table .v-data-footer__pagination { font-size: 12px; }

/* Zawartość wiersza */
.lead-person {
  min-width: 200px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.lead-avatar,
.lead-dialog-avatar {
  width: 37px;
  height: 37px;
  flex: 0 0 37px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(126, 151, 255, 0.18);
  border-radius: 12px;
  background: linear-gradient(145deg, rgba(102, 136, 255, 0.2), rgba(166, 141, 255, 0.12));
  color: #D4DCFF;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.025em;
}
.lead-person__copy { min-width: 0; display: flex; flex-direction: column; }
.lead-person__copy strong {
  max-width: 180px;
  overflow: hidden;
  color: #F6F8FC;
  font-size: 13px;
  font-weight: 850;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lead-person__copy small {
  max-width: 180px;
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 2px;
  overflow: hidden;
  color: #8995AA;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lead-person__copy small >>> .v-icon { color: #718099 !important; }
.lead-person__copy time { margin-top: 2px; color: #657188; font-size: 10.5px; font-variant-numeric: tabular-nums; }
.lead-contact { min-width: 190px; display: flex; flex-direction: column; gap: 5px; }
.lead-contact a,
.lead-contact__empty {
  max-width: 210px;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  color: #CBD3E2;
  font-size: 11.5px;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lead-contact a:hover { color: #9BB1FF; }
.lead-contact a > span {
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(102, 136, 255, 0.085);
}
.lead-contact a > span >>> .v-icon { color: #829CFA !important; }
.lead-contact__empty { color: #657188; }
.lead-contact__empty >>> .v-icon { color: #59667B !important; }
.lead-preview,
.lead-note {
  width: 100%;
  min-width: 175px;
  max-width: 250px;
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  column-gap: 6px;
  padding: 7px 8px;
  border: 1px solid rgba(255, 255, 255, 0.045);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.025);
  color: #B9C2D1;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.lead-preview:hover,
.lead-note:hover { border-color: rgba(126, 151, 255, 0.16); background: rgba(102, 136, 255, 0.055); }
.lead-preview >>> .v-icon,
.lead-note >>> .v-icon { margin-top: 1px; color: #74839B !important; }
.lead-preview > span {
  display: -webkit-box;
  overflow: hidden;
  font-size: 11.5px;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.lead-preview > small { grid-column: 2; margin-top: 3px; color: #7892EE; font-size: 10.5px; font-weight: 750; }
.lead-preview.is-empty > span,
.lead-note.is-empty { color: #657188; font-style: italic; }
.lead-note > span { min-width: 0; display: flex; flex-direction: column; }
.lead-note strong {
  display: -webkit-box;
  overflow: hidden;
  color: inherit;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.lead-note small { margin-top: 3px; overflow: hidden; color: #667289; font-size: 10px; font-style: normal; text-overflow: ellipsis; white-space: nowrap; }
.lead-status-cell { min-width: 140px; display: flex; flex-direction: column; align-items: flex-start; gap: 5px; }
.lead-status {
  --status-rgb: 126, 139, 160;
  --status-color: #A7B1C2;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border: 1px solid rgba(var(--status-rgb), 0.18);
  border-radius: 999px;
  background: rgba(var(--status-rgb), 0.09);
  color: var(--status-color);
  font-size: 10.5px;
  font-weight: 850;
  line-height: 1.15;
  white-space: nowrap;
}
.lead-status i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 3px rgba(var(--status-rgb), 0.1);
}
.lead-status--new { --status-rgb: 102, 136, 255; --status-color: #9AB0FF; }
.lead-status--progress { --status-rgb: 255, 189, 102; --status-color: #FFD08F; }
.lead-status--handled { --status-rgb: 80, 216, 144; --status-color: #82E7AE; }
.lead-status-cell > small {
  max-width: 155px;
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  color: #6B778D;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lead-status-cell > small >>> .v-icon { color: #647187 !important; }
.lead-actions { display: flex; align-items: center; justify-content: flex-end; gap: 5px; }
.lead-primary-action,
.lead-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.lead-primary-action {
  min-width: 110px;
  min-height: 32px;
  gap: 5px;
  padding: 6px 9px;
  border-radius: 9px;
  font-size: 10.5px;
  font-weight: 850;
}
.lead-primary-action >>> .v-icon { color: inherit !important; }
.lead-primary-action--progress { border-color: rgba(255, 189, 102, 0.22); background: rgba(255, 189, 102, 0.09); color: #FFD08F; }
.lead-primary-action--handled { border-color: rgba(80, 216, 144, 0.22); background: rgba(80, 216, 144, 0.09); color: #80E4AD; }
.lead-primary-action--neutral { border-color: rgba(255, 255, 255, 0.08); background: rgba(255, 255, 255, 0.04); color: #AAB4C5; }
.lead-primary-action:hover:not(:disabled) { filter: brightness(1.14); }
.lead-primary-action:disabled { opacity: 0.55; cursor: wait; }
.lead-more {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border-color: rgba(255, 255, 255, 0.065);
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.025);
  color: #8591A7;
}
.lead-more:hover { border-color: rgba(255, 255, 255, 0.13); background: rgba(255, 255, 255, 0.055); color: #FFF; }
.lead-more >>> .v-icon { color: inherit !important; }
.leads-empty { min-height: 220px; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 30px; color: #68748A; text-align: center; }
.leads-empty > span {
  width: 54px;
  height: 54px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 11px;
  border-radius: 17px;
  background: rgba(102, 136, 255, 0.09);
}
.leads-empty > span >>> .v-icon { color: #7E99F8 !important; }
.leads-empty strong { color: #C8D0DE; font-size: 13px; }
.leads-empty p { margin: 4px 0 0; font-size: 12px; }
</style>

<style scoped>
/* Dialog szczegółów */
.website-lead-dialog__card {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.085) !important;
  border-radius: 20px !important;
  background: #202534 !important;
  color: #E8ECF4 !important;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42) !important;
}
.lead-dialog-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background:
    radial-gradient(circle at 12% -40%, rgba(102, 136, 255, 0.19), transparent 45%),
    #252B3C;
}
.lead-dialog-head__identity { min-width: 0; display: flex; align-items: center; gap: 11px; }
.lead-dialog-avatar { width: 43px; height: 43px; flex-basis: 43px; border-radius: 14px; font-size: 12.5px; }
.lead-dialog-head__identity > div { min-width: 0; }
.lead-dialog-head__identity > div > span {
  color: #819BFA;
  font-size: 10.5px;
  font-weight: 850;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}
.lead-dialog-head h2 {
  margin: 2px 0 0;
  overflow: hidden;
  color: #FFF;
  font-size: 18px;
  font-weight: 900;
  letter-spacing: -0.025em;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lead-dialog-head p { margin: 3px 0 0; color: #758198; font-size: 11px; }
.lead-dialog-head__right { display: flex; flex: 0 0 auto; align-items: center; gap: 8px; }
.lead-dialog-close {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.035);
  color: #8995A9;
  cursor: pointer;
}
.lead-dialog-close:hover { background: rgba(255, 255, 255, 0.07); color: #FFF; }
.lead-dialog-close >>> .v-icon { color: inherit !important; }
.lead-dialog-body { padding: 18px 20px !important; background: #202534; color: #E5E9F1 !important; }
.lead-dialog-contact-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}
.lead-dialog-contact-grid > a,
.lead-dialog-contact-grid > div {
  min-width: 0;
  display: grid;
  grid-template-columns: 35px minmax(0, 1fr) auto;
  grid-template-rows: auto auto;
  align-items: center;
  column-gap: 8px;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.055);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.025);
  color: inherit;
  text-decoration: none;
}
.lead-dialog-contact-grid > a:hover { border-color: rgba(126, 151, 255, 0.18); background: rgba(102, 136, 255, 0.05); }
.lead-dialog-contact-grid > a > span,
.lead-dialog-contact-grid > div > span {
  width: 35px;
  height: 35px;
  grid-row: span 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(102, 136, 255, 0.095);
}
.lead-dialog-contact-grid > a > span >>> .v-icon,
.lead-dialog-contact-grid > div > span >>> .v-icon { color: #87A0FB !important; }
.lead-dialog-contact-grid small { color: #6E7A91; font-size: 10px; font-weight: 750; }
.lead-dialog-contact-grid strong {
  overflow: hidden;
  color: #E6EAF2;
  font-size: 11.5px;
  font-weight: 750;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lead-dialog-contact-grid > a > .v-icon { grid-row: span 2; color: #637089 !important; }
.lead-dialog-contact-grid > .is-empty strong { color: #6A768D; font-weight: 600; }
.lead-dialog-section {
  margin-top: 12px;
  padding: 13px;
  border: 1px solid rgba(255, 255, 255, 0.055);
  border-radius: 14px;
  background: #1C2130;
}
.lead-dialog-section > header { display: flex; align-items: center; gap: 9px; }
.lead-dialog-section > header > span {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(102, 136, 255, 0.1);
}
.lead-dialog-section > header > span >>> .v-icon { color: #809BFA !important; }
.lead-dialog-section > header > span.is-amber { background: rgba(255, 189, 102, 0.09); }
.lead-dialog-section > header > span.is-amber >>> .v-icon { color: #FFC876 !important; }
.lead-dialog-section > header > div { min-width: 0; flex: 1; }
.lead-dialog-section h3 { margin: 0; color: #EEF1F6; font-size: 12.5px; font-weight: 850; }
.lead-dialog-section header p { margin: 2px 0 0; overflow: hidden; color: #68758C; font-size: 10.5px; text-overflow: ellipsis; white-space: nowrap; }
.lead-dialog-message,
.lead-dialog-note {
  margin-top: 10px;
  padding: 11px 12px;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.026);
  color: #CAD2DF;
  font-size: 12.5px;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.lead-dialog-message.is-empty,
.lead-dialog-note.is-empty { color: #657188; font-style: italic; }
.lead-dialog-edit {
  min-height: 29px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 8px;
  border: 1px solid rgba(255, 189, 102, 0.16);
  border-radius: 9px;
  background: rgba(255, 189, 102, 0.07);
  color: #F4C57E;
  font-size: 10.5px;
  font-weight: 800;
  cursor: pointer;
}
.lead-dialog-edit >>> .v-icon { color: inherit !important; }
.lead-dialog-edit:disabled { opacity: 0.55; cursor: wait; }
.lead-dialog-section > header > span.is-blue { background: rgba(102, 136, 255, 0.1); }
.lead-dialog-section > header > span.is-blue >>> .v-icon { color: #8EA7FF !important; }

/* Tłumaczenie zapytania */
.lead-dialog-message--translated {
  border: 1px solid rgba(102, 136, 255, 0.14);
  background: rgba(102, 136, 255, 0.055);
}
.lead-dialog-message--translated small {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  color: #8EA7FF;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.lead-dialog-message--translated small >>> .v-icon { color: #8EA7FF !important; }

/* Historia odpowiedzi */
.lead-reply-list { display: grid; gap: 8px; margin-top: 10px; }
.lead-reply-item {
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.026);
}
.lead-reply-item.is-failed { border-color: rgba(255, 122, 138, 0.25); background: rgba(255, 122, 138, 0.05); }
.lead-reply-item > header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
  color: #97A3B8;
  font-size: 11px;
}
.lead-reply-item > header >>> .v-icon { color: #8EA7FF !important; }
.lead-reply-item > header strong { color: #DCE2EE; font-weight: 800; }
.lead-reply-item > header time { margin-left: auto; color: #68758C; font-size: 10.5px; }
.lead-reply-lang {
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(166, 141, 255, 0.14);
  color: #C3B3FF;
  font-size: 10px;
  font-weight: 800;
}
.lead-reply-failed {
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(255, 122, 138, 0.16);
  color: #FF9AA4;
  font-size: 10px;
  font-weight: 800;
}
.lead-reply-item p {
  margin: 7px 0 0;
  color: #CAD2DF;
  font-size: 12.5px;
  line-height: 1.55;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.lead-reply-item small {
  display: block;
  margin-top: 5px;
  color: #7C88A0;
  font-size: 11px;
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.lead-reply-item .lead-reply-by { color: #5F6B82; font-size: 10.5px; }

/* Dialog odpowiedzi */
.reply-row { display: flex; align-items: center; gap: 10px; }
.reply-row + .reply-row { margin-top: 10px; }
.reply-label {
  min-width: 96px;
  color: #8B96AA;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.reply-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.reply-chips button {
  min-height: 31px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.035);
  color: #A9B4C7;
  font-size: 11.5px;
  font-weight: 750;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.reply-chips button >>> .v-icon { color: inherit !important; }
.reply-chips button:hover:not(:disabled) { background: rgba(255, 255, 255, 0.07); color: #D6DCE8; }
.reply-chips button.is-active {
  border-color: rgba(102, 136, 255, 0.4);
  background: rgba(102, 136, 255, 0.13);
  color: #A9BCFF;
}
.reply-chips button:disabled { opacity: 0.4; cursor: not-allowed; }
.reply-field { margin-top: 12px; }
.reply-field label {
  display: block;
  margin-bottom: 5px;
  color: #8B96AA;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.reply-input,
.reply-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 11px;
  background: #1B202E;
  color: #E6EAF2;
  font-family: inherit;
  font-size: 12.5px;
  line-height: 1.55;
  outline: none;
  transition: border-color 0.15s ease;
}
.reply-input:focus,
.reply-textarea:focus { border-color: rgba(102, 136, 255, 0.55); }
.reply-input::placeholder,
.reply-textarea::placeholder { color: #6F7B91; }
.reply-textarea { resize: vertical; }
.reply-template-bar { display: flex; align-items: center; gap: 6px; }
.reply-select {
  flex: 1;
  min-width: 0;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  background-image: linear-gradient(45deg, transparent 50%, #8B96AA 50%), linear-gradient(135deg, #8B96AA 50%, transparent 50%);
  background-position: calc(100% - 18px) 50%, calc(100% - 13px) 50%;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
}
.reply-select option { background: #1B202E; color: #E6EAF2; }
.reply-template-btn {
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.035);
  color: #A9B4C7;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.reply-template-btn >>> .v-icon { color: inherit !important; }
.reply-template-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.07); color: #D6DCE8; }
.reply-template-btn.is-danger:hover:not(:disabled) {
  border-color: rgba(255, 122, 138, 0.3);
  background: rgba(255, 122, 138, 0.08);
  color: #FF9AA4;
}
.reply-template-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.reply-translate-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}
.reply-translate-bar small { color: #8B96AA; font-size: 11px; }
.reply-counter {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  color: #7C88A0;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}
.reply-counter >>> .v-icon { color: #7C88A0 !important; }
.reply-counter.is-over,
.reply-counter.is-over >>> .v-icon { color: #FF9AA4 !important; }
.reply-counter b { margin-left: 4px; font-weight: 800; }
.reply-counter__unicode { display: inline-flex; align-items: center; gap: 3px; }
.reply-counter__unicode code {
  padding: 0 4px;
  border: 1px solid rgba(255, 189, 102, 0.25);
  border-radius: 4px;
  background: rgba(255, 189, 102, 0.1);
  color: #FFD08F;
  font-size: 11px;
}
.reply-strip-row { margin-top: 12px; }
.reply-strip-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.035);
  color: #A9B4C7;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.reply-strip-toggle >>> .v-icon { color: inherit !important; }
.reply-strip-toggle.is-on {
  border-color: rgba(80, 216, 144, 0.3);
  background: rgba(80, 216, 144, 0.08);
  color: #82E7AE;
}
.reply-history { margin-top: 14px; }
.reply-history > label {
  display: block;
  margin-bottom: 2px;
  color: #8B96AA;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.lead-dialog-owner {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(80, 216, 144, 0.12);
  border-radius: 12px;
  background: rgba(80, 216, 144, 0.055);
}
.lead-dialog-owner > span {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(80, 216, 144, 0.09);
}
.lead-dialog-owner > span >>> .v-icon { color: #72DEA3 !important; }
.lead-dialog-owner > div { min-width: 0; flex: 1; }
.lead-dialog-owner small { display: block; color: #669278; font-size: 10px; }
.lead-dialog-owner strong { display: block; overflow: hidden; color: #BFE9D1; font-size: 11.5px; text-overflow: ellipsis; white-space: nowrap; }
.lead-dialog-owner time { color: #658372; font-size: 10.5px; white-space: nowrap; }
.lead-dialog-actions {
  min-height: 62px;
  gap: 7px;
  padding: 11px 20px !important;
  border-top: 1px solid rgba(255, 255, 255, 0.055);
  background: #1B202E;
}
.lead-dialog-actions > span { flex: 1; }
.lead-dialog-delete,
.lead-dialog-status-action {
  min-height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 6px 10px;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.035);
  color: #A8B2C4;
  font-size: 10.5px;
  font-weight: 850;
  cursor: pointer;
}
.lead-dialog-delete { border-color: rgba(242, 118, 130, 0.16); background: rgba(242, 118, 130, 0.06); color: #F4969F; }
.lead-dialog-status-action.is-new { border-color: rgba(102, 136, 255, 0.19); background: rgba(102, 136, 255, 0.08); color: #9BB1FF; }
.lead-dialog-status-action.is-progress { border-color: rgba(255, 189, 102, 0.2); background: rgba(255, 189, 102, 0.08); color: #FFD08F; }
.lead-dialog-status-action.is-handled { border-color: rgba(80, 216, 144, 0.2); background: rgba(80, 216, 144, 0.08); color: #82E7AE; }
.lead-dialog-delete >>> .v-icon,
.lead-dialog-status-action >>> .v-icon { color: inherit !important; }
.lead-dialog-delete:hover,
.lead-dialog-status-action:hover:not(:disabled) { filter: brightness(1.15); }
.lead-dialog-status-action:disabled { opacity: 0.55; cursor: wait; }

/* Dostępność i responsywność */
.leads-refresh:focus-visible,
.leads-status-row:focus-visible,
.leads-tab:focus-visible,
.lead-person:focus-visible,
.lead-preview:focus-visible,
.lead-note:focus-visible,
.lead-primary-action:focus-visible,
.lead-more:focus-visible,
.lead-dialog-close:focus-visible,
.lead-dialog-edit:focus-visible,
.lead-dialog-delete:focus-visible,
.lead-dialog-status-action:focus-visible {
  outline: 2px solid #8EA7FF;
  outline-offset: 2px;
}
@keyframes lead-spin { to { transform: rotate(360deg); } }
@keyframes lead-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.82); }
}
@keyframes lead-shimmer { to { background-position-x: -200%; } }
@media (max-width: 1180px) {
  .leads-metric__copy { grid-template-columns: 1fr; }
  .leads-metric__copy strong { grid-row: auto; grid-row-start: 1; font-size: 24px; }
  .leads-metric__copy small { grid-row-start: 2; }
  .leads-metric__copy span { display: none; }
}
@media (max-width: 960px) {
  .website-leads-page { padding-right: 18px; padding-left: 18px; }
  .leads-hero__content { align-items: flex-start; flex-direction: column; }
  .leads-hero__actions { width: 100%; }
  .leads-inbox-state { flex: 1; }
  .leads-insights { grid-template-columns: 1fr; }
  .leads-workspace__header { align-items: stretch; flex-direction: column; }
  .leads-search { width: 100%; max-width: none; flex-basis: auto; }
}
@media (max-width: 700px) {
  .website-leads-page { min-height: calc(100vh - 48px); margin: -16px; padding: 14px 12px 34px; }
  .leads-hero { padding: 17px; border-radius: 18px; }
  .leads-identity { align-items: flex-start; }
  .leads-identity__icon { width: 46px; height: 46px; flex-basis: 46px; border-radius: 14px; }
  .leads-identity h1 { font-size: 23px; }
  .leads-identity p { font-size: 12.5px; }
  .leads-hero__actions { align-items: stretch; flex-direction: column; }
  .leads-refresh { width: 100%; }
  .leads-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .leads-metric { min-height: 86px; padding: 12px; }
  .leads-metric__icon { width: 35px; height: 35px; flex-basis: 35px; }
  .leads-workspace { padding: 13px; border-radius: 17px; }
  .leads-tabs { align-items: stretch; }
  .leads-tab { flex: 1 0 auto; }
  .leads-results-bar { align-items: flex-start; flex-direction: column; }
  .leads-results-bar__search { max-width: 100%; }
  .website-leads-page >>> .leads-table .v-data-table__mobile-row {
    min-height: 36px;
    align-items: flex-start;
    padding: 7px 11px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.035);
  }
  .website-leads-page >>> .leads-table .v-data-table__mobile-row__header {
    flex: 0 0 92px;
    color: #6E7A91;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .website-leads-page >>> .leads-table .v-data-table__mobile-row__cell { min-width: 0; flex: 1; text-align: left; }
  .website-leads-page >>> .leads-table tbody tr {
    display: block;
    margin-bottom: 8px;
    border-bottom: 1px solid rgba(126, 151, 255, 0.12);
  }
  .lead-person,
  .lead-contact,
  .lead-status-cell { min-width: 0; }
  .lead-preview,
  .lead-note { max-width: none; }
  .lead-actions { justify-content: flex-start; }
  .lead-dialog-head { align-items: flex-start; }
  .lead-dialog-head__right { align-items: flex-end; flex-direction: column-reverse; }
  .lead-dialog-contact-grid { grid-template-columns: 1fr; }
  .lead-dialog-actions { align-items: stretch; flex-direction: column; }
  .lead-dialog-actions > span { display: none; }
  .lead-dialog-delete,
  .lead-dialog-status-action { width: 100%; }
}
@media (max-width: 440px) {
  .leads-metrics { grid-template-columns: 1fr; }
  .leads-metric { min-height: 74px; }
  .leads-metric__copy { grid-template-columns: 1fr auto; }
  .leads-metric__copy strong { grid-row: span 2; font-size: 25px; }
  .leads-metric__copy small { grid-row: auto; }
  .leads-chart-bars { gap: 2px; }
  .leads-status-row { grid-template-columns: 7px 84px minmax(60px, 1fr) 24px; }
  .lead-dialog-head__right .lead-status { display: none; }
}
</style>

<style>
.website-leads-menu {
  overflow: hidden !important;
  border: 1px solid rgba(255, 255, 255, 0.085) !important;
  border-radius: 12px !important;
  background: #252B3B !important;
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.38) !important;
}
.website-leads-menu .v-list { padding: 5px !important; background: #252B3B !important; color: #DCE2EC !important; }
.website-leads-menu .v-list-item { min-height: 36px !important; border-radius: 8px; color: #C7CFDD !important; }
.website-leads-menu .v-list-item:hover { background: rgba(255, 255, 255, 0.055) !important; }
.website-leads-menu .v-list-item__icon { min-width: 28px !important; margin: 9px 0 !important; }
.website-leads-menu .v-list-item__icon .v-icon { color: #8996AB; }
.website-leads-menu .v-list-item__title { font-size: 12px !important; font-weight: 650; }
.website-leads-menu .v-subheader {
  height: 28px !important;
  padding: 0 10px !important;
  color: #68758C !important;
  font-size: 10px !important;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.website-leads-menu .v-divider { border-color: rgba(255, 255, 255, 0.065) !important; }
.website-leads-menu__danger,
.website-leads-menu__danger .v-list-item__title { color: #FF9AA4 !important; }
.website-lead-dialog { border-radius: 20px !important; }
</style>
